// src/expenseItems.js
// ─────────────────────────────────────────────────────────────
// What was bought, and where (10/9/26) — pure: no React, no network, no
// imports. The import engine (src/expenseImport.js) and the Expenses screen
// (src/views/Expenses.jsx) both use it, so there is ONE item taxonomy.
//
// Reece: "I'd also like to be able to identify things like: paper bought at
// Amazon versus paper bought at Staples; envelopes bought on Amazon versus
// envelopes bought at Staples; and so on and so forth."
//
//   itemTypeOf(text)   an Amazon title, a store receipt line ("ENV #10 P&S
//                      SECURI", "RESUME PPR WHT 100", "CANON GI25 CMY 3PK"),
//                      an email subject or a typed description → one item
//                      type (ITEM_TYPES, or a named software item such as
//                      "Claude"), or null. A store's NAME never decides it —
//                      "Staples — Here's your Staples receipt" is no item.
//   vendorGroupOf(v)   Amazon / Amazon Business / AMAZON MKTPL → "Amazon",
//                      "STAPLES 00123" → "Staples", Office Depot / OfficeMax,
//                      USPS, …, else the vendor's own (tidied) name.
//   packCountOf(text, unitName?)
//                      units in ONE of what was bought ("500 count", "Pack of
//                      100", "500 Sheets", "100/Box", "250 ct", "500#10",
//                      "Ream" = 500 sheets; "500/Box, 2 Boxes" = 1,000; an
//                      explicit "(5000 Sheets)" wins; labels = "N labels"
//                      or labels per sheet × sheets) or null — never a
//                      guess: counts that don't fit together are unknown.
//   itemsLine(items, rowAmount)
//                      the one notes line an import writes on a row with line
//                      items: "Items: Paper $53.98 (2 · 200 sheets) ·
//                      Envelopes $68.98 (1 · 500 envelopes) · Ink & Toner
//                      $63.19 (2)". Each type = its lines + a proportional
//                      share of the row's tax / shipping / promotions /
//                      points, so the parts sum EXACTLY to the row (pennies on
//                      the largest). Free / 100%-off lines and points are
//                      folded in, never types of their own.
//   parseItemsLine(notes)
//                      reads it back — tolerant, so Reece can type "Items:
//                      Paper $20 · Envelopes $30" by hand (a part typed with
//                      no amount gets what's left of the row).
//   expenseItemParts(expense) / whereYouBuy(expenses)
//                      the Expenses breakdowns: "What it went on" chips and
//                      the "Where you buy it" table (item type × vendor: $,
//                      purchases, and a unit cost only when the units of
//                      EVERY dollar in that cell are known).
//   MARKETING_SUBCATEGORIES / marketingSubOf(type) / subcategoryParts(ex)
//                      Marketing's subcategories (10/9/26, Reece: "for
//                      marketing, i want to have things separated out for
//                      "Data" "Postage" Paper" Evenlopes" and have all of
//                      those be categorized under marketing, but be able to
//                      separate things out under subcategories"). They ARE
//                      the item types — Data, Postage, Paper, Envelopes,
//                      Cardstock, Ink & Toner, Printing, Business Cards,
//                      Labels, Pens & Markers, Mailing Machine — plus "Other
//                      marketing" for any other part of a Marketing row, so
//                      a Marketing row's subcategories add up to its amount
//                      to the cent.
//                      (10/9/26, later — Reece: "Can you also include the
//                      mailhouse fees under postage?": the mailhouse —
//                      AlphaGraphics, mail house, presort, letter shop, EDDM,
//                      a "mailing" charge — IS Postage; old "Mailhouse" Items
//                      lines read as Postage. Business cards are their own
//                      subcategory, "Business Cards" (Vistaprint, plastic /
//                      linen / cotton cards), counted in cards.)
//   varietyOf(type, title) / VARIETIES
//                      (10/10/26 — Reece: "identify things like types of
//                      paper (for example, standard paper, 28 lb. paper,
//                      versus the standard envelopes, versus the more
//                      expensive Southworth envelopes…") the KIND of paper /
//                      envelope / cardstock / ink / business card / label a
//                      title is: "Cotton (25–100%)", "Standard #10 security",
//                      "65 lb", "Canon GI-26"… — weight (digits + lb / #),
//                      brand and finish words decide, Staples' receipt
//                      abbreviations included (RESUME PPR, FINE LINEN, FINE
//                      PARCH, S/W = Southworth, ENV #10 P&S SECURI, EXCEPT
//                      RESUME ENV, COVER 8.5X11). An Items line part is
//                      (type, variety): "Paper [Cotton (25–100%)] $53.98 (2 ·
//                      200 sheets)" — old lines without "[…]" read as before.
//   brandOf(title) / a part's brand
//                      (10/10/26, later — Reece: "for the paper types, you
//                      can just break it down to Southworth paper versus
//                      non-Southworth paper. That would likely be sufficient
//                      to get an idea of the cost.") Paper, Envelopes and
//                      Cardstock parts carry a BRAND: "Southworth" (the name,
//                      Staples' "S/W", a Southworth item number — JD18C,
//                      J404N10, Z550CK… — and, 10/11/26, Staples' FINE
//                      LINEN / FINE PARCH / RESUME PPR / EXCEPT RESUME ENV /
//                      COVER 8.5X11 short names and UPC 083514…: "yes, all
//                      of those are southworth styles"), "Non-Southworth"
//                      (the line NAMES another brand — Hammermill, Amazon
//                      Basics, Staples' own… — or the Amazon report's Brand
//                      column does), or none ("No brand detail": no brand
//                      named — a store's "copy paper" — never a guess). In
//                      the Items line it rides inside the kind's
//                      brackets: "Paper [Cotton (25–100%) · Southworth]
//                      $53.98 (2 · 200 sheets) · Envelopes [Standard #10
//                      security · Non-Southworth] $27.59 (1)"; a line without
//                      it reads exactly as before (no brand).
//   Postage kinds / stampLineFacts / withPartUnits
//                      (10/11/26 — point-in-time cost per letter,
//                      src/supplyLots.js) Postage has kinds too: Letter
//                      stamps, Add-on stamps (not a letter), Parcel &
//                      shipping (not a letter), Postage balance (Stamps.com),
//                      Mailhouse (all-in). A receipt LINE's kind and letter-
//                      stamp count come from its price (stampLineFacts); a
//                      line may carry its count outright (`units`); a hand
//                      row's description count is its count (handCountOf —
//                      never an imported row's); withPartUnits puts a count
//                      on one part of a row's Items line.
// ─────────────────────────────────────────────────────────────

var ITEMS_PREFIX="Items: ";
var ITEM_OTHER="Other";
var NO_DETAIL="No item detail";
var NO_VENDOR="No vendor";
var OTHER_VENDORS="__other";   // the "Other" column's key (never a vendor's name)
var REVIEW_LINE="⚑ Review: ";  // expenseImport's REVIEW_PREFIX (that module imports this one)
var OTHER_MARKETING="Other marketing";

// The panel's rows, in order: mailer supplies first. `unit` = what a pack
// count counts for that type (null = not counted — ink, pens…). The names
// are Reece's Marketing subcategories (10/9/26): "Postage" (was Stamps &
// Postage — and the mailhouse folds into it: AlphaGraphics, presort, letter
// shop, EDDM), "Data" (was Lists & Data), "Business Cards" (its own,
// counted in cards — no longer Printing).
var ITEM_TYPES=[
  {type:"Paper",unit:"sheets",supply:true},
  {type:"Cardstock",unit:"sheets",supply:true},
  {type:"Envelopes",unit:"envelopes",supply:true},
  {type:"Ink & Toner",unit:null,supply:true},
  {type:"Labels",unit:"labels",supply:true},
  {type:"Postage",unit:"stamps",supply:true},
  {type:"Pens & Markers",unit:null,supply:true},
  {type:"Mailing Machine",unit:null,supply:true},   // inserter / sealer / folder + their brushes and solution
  {type:"Printing",unit:null},
  {type:"Business Cards",unit:"cards"},
  {type:"Data",unit:null},
  {type:"Office Supplies",unit:null},
  {type:"Equipment",unit:null},
  {type:"Software",unit:null},
  {type:"Phone",unit:null},
  {type:"Internet",unit:null},
  {type:ITEM_OTHER,unit:null},
  {type:NO_DETAIL,unit:null}
];
var TYPE_INFO={};ITEM_TYPES.forEach(function(t,i){TYPE_INFO[t.type]=Object.assign({order:i},t);});
var UNIT_ONE={sheets:"sheet",envelopes:"envelope",labels:"label",stamps:"stamp",cards:"card"};

// Software that was always its own item (the old "What it went on" chips:
// Claude, RingCentral, Adobe, MagicBlocks) and the rest of the SaaS Reece
// pays for. Each is its own item / chip; in the "Where you buy it" table
// they are one row, Software.
var NAMED_SOFTWARE=[
  [/claude|anthropic/,"Claude"],[/ringcentral|ring central/,"RingCentral"],[/adobe/,"Adobe"],[/magicblocks|magic blocks/,"MagicBlocks"],
  [/supabase/,"Supabase"],[/vercel/,"Vercel"],[/eleven ?labs/,"ElevenLabs"],[/wispr/,"Wispr Flow"],[/\befax\b/,"eFax"],
  [/openai|chatgpt/,"OpenAI"],[/\bcanva\b/,"Canva"],[/zoom\.us|zoom video|\bzoom (pro|workplace|one)\b/,"Zoom"],[/dropbox/,"Dropbox"],
  [/godaddy/,"GoDaddy"],[/microsoft (365|office)|office 365|\bmsft\b/,"Microsoft"],[/google (workspace|one)\b|g ?suite/,"Google"],[/qr\.io/,"QR.io"]
];
var NAMED={};NAMED_SOFTWARE.forEach(function(r){NAMED[r[1]]=true;});

// ── Text → item type ─────────────────────────────────────────

// Lower-cased, with the things that trip the rules out of the way: bidi
// marks Amazon puts round a count ("⁦25⁩"), ® / ™, the "Mr. Pen" tape brand,
// and "paper" words that aren't paper (Paper Mate pens, paper clips,
// paper cutters).
function cleanText(text){
  var s=String(text==null?"":text).toLowerCase()
    .replace(/[​-‏⁦-⁩®™]/g,"")
    .replace(/\bmr\.?\s*pen\b/g," ")
    .replace(/paper ?mate/g,"papermate")
    .replace(/paper (clips?|cutters?|trimmers?)/g,"paper$1")
    .replace(/\s+/g," ");
  return" "+s.trim()+" ";
}
// More than one thing ("Ordered: 13 Office items", "… and 15 more items",
// "(+2 more)", a receipt's "4 more items" line) — no single type.
var MIXED_RE=/\bitems\b|\(\+\d+ more\)|\band \d+ more\b|\b\d+ more item\b/;
// Household / personal things whose words would otherwise hit a rule
// (toilet PAPER, a pet hair brush, a game CONTROLLER).
var NOT_ITEM_RE=/toilet|paper towels?|facial tissues?|\btissues?\b|paper (plates?|cups?|bowls?|bags?)|napkins?|baking|wax paper|gift wrap|wrapping paper|toothpaste|mouthwash|shampoo|\bdog\b|\bcats?\b|\bpet\b|puppy|sports drink|energy drink|nasal|playstation|xbox|nintendo|controller/;
// A bare "COVER" is a cover-stock receipt line ("COVER 8.5X11 WHT") — not a
// phone / dust / seat cover, a cover letter or a fax cover sheet.
function bareCover(s){
  return/\bcover\b/.test(s)&&!/(phone|laptop|keyboard|dust|seat|book|cushion|bed|table|car|printer|chair|monitor|screen|tablet|ipad|mattress|pillow|toilet|case|lens|grill|outlet|switch|plate)\s?covers?\b|\bcovers?\s?(for|case|letter|sheet)\b/.test(s);
}
// A printer / scanner itself, not something for one ("… Compatible with
// MegaTank GX7120 Printers" is ink).
function printerItself(s){
  return/\b(printers?|scanners?|copiers?)\b/.test(s)&&!/compatible|for use with|for (hp|canon|epson|brother)\b|replacement|cartridge|\binks?\b|toner|refill|maintenance|\bgi[- ]?\d|\bpg[- ]?\d|\bcl[- ]?\d|paper|cable|labels?|stand\b|cover/.test(s);
}
// Astrobrights is a brand, not a weight: its cardstock (65 lb, "Index",
// "Cover") is Cardstock, but "Astrobrights Premium Inkjet and Laser Paper"
// / "Color Paper, 24 lb" / copy paper is Paper (10/10/26 — the Target Uber
// deliveries' 300 ct Inkjet and Laser Paper read as Cardstock). A bare
// "ASTROBRIGHTS" receipt line stays Cardstock (what he buys of it).
var CARDSTOCK_RE=/card ?stock|cover ?stock|astro ?brights\b(?!.*(\bpapers?\b|\bppr\b|inkjet|laser|\bcopy\b|multipurpose|\btext\b|\b[1-5]\d ?-? ?(lbs?|#)(?![a-z])))|cover weight|\b\d{2,3} ?(lbs?|#)\.? ?cover\b|\b(6[5-9]|[7-9]\d|1[0-4]\d) ?-? ?(lbs|lb|#)(?![a-z])(?!\.? *text)/;
// (+ 10/10/26, the Staples receipts in the invoice export: "STAPLES 30% REC
// 32" — recycled paper — and "S/W BARE WHITE 32L" — Southworth)
var PAPER_RE=/\b\d{2,3} ?% rec(ycled)?\b|\bs\/w (bare|bright|linen|cotton|ivory|white|natural|parch)|\bpapers?\b|\bppr\b|\bresume\b|r[eé]sum[eé]|\d+ ?% cotton|\bcotton (business|linen|resume|bond|paper|stationery|fiber)|\blinen\b|parchment|fine (paper|linen|parchment|stationery)|southworth|hammermill|letterhead|statione?ry|stationary|\breams?\b|multipurpose|\bcopy paper|laser paper|bond paper|\b8\.?5 ?x ?11\b|\b8 1\/2"? ?x ?11\b|\b(line|lin)\b(?=.*\b(8\.?5|wht|white|ivory|natural|24 ?lb|32 ?lb)\b)/;
var PENS_RE=/papermate|\bpens?\b|fountain pen|roller ?ball|ball ?point|gel pen|felt[- ]tip|\bmarkers?\b|sharpie|highlighters?|writing instruments?|\bnib\b|waterman|scriveiner|wordsworth|uni-?ball|pilot (g-?2|precise|v5|v7)|pen refills?|\bink refills?\b|calligraphy|\bquills?\b/;
var INK_RE=/\binks?\b|toner|cartridges?|\bgi[- ]?\d{2,3}\b|\bpg[- ]?\d{2,3}[a-z]{0,2}\b|\bcl[- ]?\d{2,3}[a-z]{0,2}\b|\bhp ?\d{2,3}[a-z]{0,2}\b(?!\s*(?:"|''|”|-? ?inch|in\b))|megatank|ecotank|ink ?bottles?|inkjet (cartridges?|ink)|drum unit|maintenance (box|cartridge|kit)|\bcmy\b|\btri-?colou?r\b/;
// (AlphaGraphics is the mailhouse — Postage, below; Vistaprint is business
// cards unless the text says otherwise — the last rule.)
var PRINTING_RE=/print shop|printing|print on demand|fedex office|\bprinted\b|post ?cards?|flyers?|brochures?|door ?hangers?|print job|\bcopies\b|custom print|\bbanners?\b|\bprints\b/;
// A print JOB, tried before Paper: "Flyers 8.5x11", "Letterhead printing"
// are something printed for him, not paper he bought. Narrow on purpose —
// "Brochure paper", "Printing paper" and an ink bottle "for vivid printing"
// are still supplies.
var PRINT_JOB_RE=/\b(flyers?|brochures?|door ?hangers?)\b(?! ?(papers?|card ?stock|stock)\b)|\b(letterhead|flyers?|brochures?|postcards?|mailers?|banners?|signs?) printing\b|\bprinting (of|services?|jobs?|order)\b|print (shop|job)|custom print(ed|ing)?\b|\bprinted (flyers?|letterhead|brochures?|postcards?|mailers?|letters?)\b/;
// The mailhouse IS postage (Reece, 10/9/26: "Can you also include the
// mailhouse fees under postage?"). AlphaGraphics of Gilbert is his mailhouse
// — every estimate / invoice is "for Mailing" — so the vendor alone says it,
// like USPS. Tried right after the stamps / postage words.
var MAILHOUSE_RE=/alpha ?graphics|mail ?house|mailing service|letter ?shop|presort|\beddm\b|every door direct|direct mail (service|vendor)|mail ?shop|mail processing/;
// A bare "mailing" charge ("Invoice 286180 Mailing", "July mailing") is the
// mailhouse too — never a mailing LIST (Data), labels, envelopes, bags or a
// mailing machine (all tried first, or excluded here).
var MAILING_RE=/\bmailings?\b(?! ?(lists?|labels?|bags?|tubes?|boxes|box|supplies|envelopes?|machines?|systems?|address(es)?|scales?|sleeves?|cartons?))/;
// Business cards (their own subcategory, 10/9/26): "Standard Business Cards",
// "White Plastic Business Cards", "Cotton Business Cards", "linen cards".
var BUSINESS_CARDS_RE=/business ?cards?|\b(plastic|linen|cotton|kraft) cards?\b/;
// Postage: stamps, USPS, the post office, Click-N-Ship…
var POSTAGE_WORDS_RE=/\bstamps?\b|postage|\busps\b|stamps\.com|postal service|post office|click-?n-?ship|priority mail|first[- ]class (mail|postage)/;
// …but not what the post office sells that ISN'T a mailing: a PO box, a
// passport (photos / fee), a notary, a money order (10/10/26 — "USPS PO Box
// rental" and "Post office - passport photos" read as Postage, and a hand
// row like that was flagged "replaced by the USPS receipts").
var POSTAL_SERVICE_RE=/\bp\.? ?o\.? ?box(es)?\b|post office box|\bpassports?\b|\bnotar(y|ies|ize|ized|ization)\b|money ?orders?/;
function isPostalService(text){return POSTAL_SERVICE_RE.test(cleanText(text));}
// Data: the lists and property / lead data a mailer goes to (PropertyShark,
// DataZapp, PropStream, PropRadar, skip tracing…). "Melissa" only as Melissa
// Data (a bare "Melissa" is a person).
var LISTS_RE=/datazapp|property ?shark|prop ?stream|prop ?radar|batch ?(leads|data|skip)|listsource|melissa ?data|skip[- ]?trac|\b(mailing|lead|leads|data|prospect|marketing|direct mail) lists?\b|list (purchase|rental|order)|data append|\bleads? data\b/;
// A phone itself (hardware) — tried before the phone BILL below.
var PHONE_HARDWARE_RE=/\b(desk|conference|cordless|landline) ?phones?\b|smartphones?|\biphones?\b|\bphones? (case|charger|stand|mount|holder|cable|cord|grip|screen protector)s?\b/;
// A bare "Phone" / "Monthly phone" is the phone bill (the old chip showed it
// as its own item; Equipment is the hardware above).
var PHONE_RE=/verizon|cell ?phone|t-?mobile|\bat ?& ?t\b|\bsprint\b|\bphones?\b|wireless (bill|plan|service)|mobile plan/;
var INTERNET_RE=/\bcox\b|internet|comcast|xfinity|spectrum|centurylink|starlink|google fiber|\bwi-?fi (service|plan)/;
// Never a bare "membership" (NRMLA, the Chamber, Costco are not software):
// only Amazon's Prime (Business) membership.
var SOFTWARE_RE=/software|subscription|\bsaas\b|license key|\bapps?\b|web ?hosting|domain (name|renewal|registration)|prime( business)?( annual)? membership|annual plan|monthly plan|\bcloud\b/;
// "WIRE LGL" on a receipt is wire binding — never a wire transfer / wire fee.
// (+ 10/10/26: "STAPLES #1 CLP" = clips; storage bins / totes)
var OFFICE_RE=/\bclps?\b|storage (bins?|boxes|totes?|containers?)|\b(storage|plastic) totes?\b|binders?|binding|wire[- ]?(o|bound|binding)\b|binding wire|\bwire (lgl|ltr|legal|letter)\b|stapler|staple remover|\b(standard|chisel point|heavy[- ]duty|box of) staples\b|paper ?clips?|binder clips?|\bclips?\b|\btapes?\b|\bglue\b|sticky notes?|post-?its?|note ?pads?|legal pads?|\blgl\b|desk ?(pad|p|mat|blotter|organi[sz]er)\b|\bpads?\b|scissors|rubber bands?|file (box|folders?|jackets?)|\bfolders?\b|hanging files?|dividers?|index cards?|clipboards?|organi[sz]ers?|office supplies?|letter opener|hole punch|white-?out|correction tape|push ?pins|thumb ?tacks|\bsharpener/;
// Whole words: "Credit monitoring", a "Guest speaker fee" and an "armchair
// travel" talk are not equipment.
// (+ 10/10/26: the Best Buy desktop, the printer's protection plan, an
// extension cord)
var EQUIPMENT_RE=/\bdesktops?\b(?! ?(organi[sz]ers?|files?|sorters?|pads?|mats?|stands?|shel(f|ves)|calendars?|easels?|labels?|trays?))|\bomnidesk\b|protection plan|extended (warranty|service)|\bwarranty\b|extension cords?|papercutters?|papertrimmers?|shredder|computer|laptop|\bmonitors?\b|keyboard|\bmouse\b|webcam|headset|\bdesk\b|\bchairs?\b|furniture|\btablet\b|\bipad\b|macbook|\bhubs?\b|\busb\b|\bcables?\b|adapter|charger|hard drive|\bssd\b|router|calculator|\blamp\b|whiteboard|filing cabinet|bookshelf|shelving|camera|\bspeakers?\b(?! (fee|fees|honorarium))|microphone|surge protector|power strip|batter(y|ies)|label ?maker/;
// First match wins — most specific first. Envelope / cardstock titles are
// full of paper words ("Southworth 25% Cotton #10 Business Envelope",
// "Southworth … Linen Copy/Inkjet/Laser Coverstock 65 lbs"), so they're
// tried before Paper; pen-ink cartridges before printer ink; paper and
// cardstock before ink ("… Inkjet …") and printers.
var RULES=[
  [/inserter|sealing solution|sealer (solution|fluid)|envelope sealer|\bmoisten|mailing system|folding machine|folder[ -]?inserter|neopost|quadient|postage meter|idealseal/,"Mailing Machine"],
  [/self[- ]?inking|rubber stamp|stamp pad|address stamp|date stamp|\bink pad|notary (stamp|seal)|\bembosser/,"Office Supplies"],
  [/label (maker|printer|writer)/,"Equipment"],
  [BUSINESS_CARDS_RE,"Business Cards"],
  [function(s){return POSTAGE_WORDS_RE.test(s)&&!POSTAL_SERVICE_RE.test(s);},"Postage"],
  [MAILHOUSE_RE,"Postage"],
  [/\blabels?\b|\blbls?\b|\bavery \d{4}/,"Labels"],
  [/envelopes?|\b(env|envs|envl|envlp|envlps|envel)\b|#\s?10\b|security tint|\bp ?& ?s\b|peel (&|and) (seal|stick|press)|\bsecuri\b/,"Envelopes"],
  [CARDSTOCK_RE,"Cardstock"],[bareCover,"Cardstock"],
  [PRINT_JOB_RE,"Printing"],
  [PAPER_RE,"Paper"],
  [PENS_RE,"Pens & Markers"],
  [printerItself,"Equipment"],
  [INK_RE,"Ink & Toner"],
  [PRINTING_RE,"Printing"],
  [LISTS_RE,"Data"],
  [MAILING_RE,"Postage"],
  [PHONE_HARDWARE_RE,"Equipment"],
  [PHONE_RE,"Phone"],
  [INTERNET_RE,"Internet"]
].concat(NAMED_SOFTWARE).concat([
  [SOFTWARE_RE,"Software"],
  [OFFICE_RE,"Office Supplies"],
  [EQUIPMENT_RE,"Equipment"],
  [/\boffice\b(?! ?(depot|max))/,"Office Supplies"],   // the old "office" chip rule
  // Vistaprint is where he buys business cards — but only when nothing else
  // in the text says what it was (a Vistaprint mouse pad, flyer or banner
  // is what it says), so it's the very last rule.
  [/vistaprint/,"Business Cards"]
]);
// → an item type ("Paper", "Envelopes", "Claude"…) or null (nothing in the
// text says what was bought — a store receipt, a mixed order, a lunch).
function itemTypeOf(text){
  var s=cleanText(text);
  if(!s.trim())return null;
  if(MIXED_RE.test(s)||NOT_ITEM_RE.test(s))return null;
  for(var i=0;i<RULES.length;i++){
    var r=RULES[i][0];
    if(typeof r==="function"?r(s):r.test(s))return RULES[i][1];
  }
  return null;
}
// A type name as typed ("envelopes", "Toner", "stamps") → the canonical one;
// a name that isn't any item type stays as typed. The names before 10/9
// ("Stamps & Postage", "Lists & Data") read as today's; so does "Mailhouse"
// (folded into Postage, 10/9/26 later). "Business Cards" is its own again.
var TYPE_ALIASES={stamps:"Postage",stamp:"Postage","stamps & postage":"Postage","stamps and postage":"Postage",ink:"Ink & Toner",toner:"Ink & Toner",pens:"Pens & Markers",markers:"Pens & Markers",
  "card stock":"Cardstock",office:"Office Supplies","office supplies":"Office Supplies",printer:"Equipment",computer:"Equipment",furniture:"Equipment",
  stationery:"Paper",lists:"Data","lists & data":"Data","lists and data":"Data","business card":"Business Cards",cards:"Business Cards",
  mailhouse:"Postage","mail house":"Postage",mailing:"Postage","no detail":NO_DETAIL};
function canonicalType(text){
  var t=oneLine(text).replace(/[:\-–—]+$/,"").trim();
  if(!t)return null;
  var l=t.toLowerCase();
  for(var k in TYPE_INFO)if(k.toLowerCase()===l)return k;
  for(var n in NAMED)if(n.toLowerCase()===l)return n;
  if(l===OTHER_MARKETING.toLowerCase())return OTHER_MARKETING;
  if(TYPE_ALIASES[l])return TYPE_ALIASES[l];
  return itemTypeOf(t)||t;
}
// The "Where you buy it" row an item type belongs to: itself, Software for
// a named software item, Other for anything else (a hand-typed "Widgets").
function itemRowOf(type){
  if(!type)return ITEM_OTHER;
  if(TYPE_INFO[type])return type;
  if(NAMED[type])return"Software";
  return ITEM_OTHER;
}
function unitNameOf(type){var t=TYPE_INFO[type];return t?t.unit:null;}
function isSupplyType(type){var t=TYPE_INFO[type];return!!(t&&t.supply);}

// ── Varieties: what KIND of paper / envelope / … (10/10/26) ──
// Reece: "identify things like types of paper (for example, standard paper,
// 28 lb. paper, versus the standard envelopes, versus the more expensive
// Southworth envelopes, and all that type of stuff)". A small set per type,
// derived from the real titles (his Amazon Business report, the Staples
// receipt lines, the invoice export's item lines, the Vistaprint cards).
// Each title gets exactly one — the first rule that fits, most specific
// first; a title that names a product no rule places is "Other <type>"; a
// line that says nothing beyond the type ("PAPER" — no weight, no finish)
// has none ("No type detail" in the explorer; Staples' "COVER 8.5X11 WHT L"
// is Southworth cover stock since 10/11/26 — Premium, like Z550CK). Ink &
// Toner is by cartridge family ("Canon GI-26", "Canon toner 075" — the
// name is built from the title). `note` = what's in it (tooltips).
var NO_TYPE_DETAIL="No type detail";
var V_STD_COPY="Standard copy (20–24 lb)",V_28="Premium 28 lb",V_32="32 lb / résumé",V_COTTON="Cotton (25–100%)",V_LINEN="Linen",V_PARCH="Parchment & specialty";
var V_STD_ENV="Standard #10 security",V_PREM_ENV="Premium (Southworth / cotton / linen)",V_RES_ENV="Résumé / matching",V_COLORED="Colored",V_WINDOW="Window",V_LARGE="6x9 & larger";
var V_65="65 lb",V_HEAVY="80–110 lb",V_PREM_CARD="Premium (linen / parchment)";
var V_MAINT="Maintenance cartridge";
var VARIETIES={
  "Paper":[
    {v:V_STD_COPY,note:"copy, printer, laser and multipurpose paper — 20–24 lb, or no weight on the title"},
    {v:V_28,note:"28 lb paper"},
    {v:V_32,note:"32 lb laser / premium paper and résumé paper that isn't cotton, linen or parchment (Staples RESUME PPR, S/W 32L, 30% REC 32)"},
    {v:V_COTTON,note:"25% – 100% cotton business and résumé paper (Southworth JD18C, 404C, résumé packs)"},
    {v:V_LINEN,note:"linen finish — Staples FINE LINEN, cotton linen paper"},
    {v:V_PARCH,note:"parchment, granite and other specialty finishes (Staples FINE PARCH, Southworth 9x4C)"},
    {v:"Other paper",note:"a paper the title names but no rule places"}],
  "Envelopes":[
    {v:V_STD_ENV,note:"plain / security-tint #10 — Amazon Basics, Blue Summit, Columbian, Quality Park, Staples ENVELOPE SECURITY / ENV #10 P&S"},
    {v:V_PREM_ENV,note:"Southworth, cotton, linen, parchment, LUXPaper text-weight, JAM premium — the more expensive ones"},
    {v:V_RES_ENV,note:"résumé envelopes that match résumé paper (Staples EXCEPT RESUME ENV, Southworth R1410L)"},
    {v:V_COLORED,note:"navy, blue, kraft, pastel… envelopes"},
    {v:V_WINDOW,note:"window envelopes"},
    {v:V_LARGE,note:"6x9, 9x12, 10x13, catalog / booklet envelopes"},
    {v:"Other envelopes",note:"#9 and anything else the title names but no rule places"}],
  "Cardstock":[
    {v:V_65,note:"65 lb (and 60–79 lb) cover — Staples 65LB, Astrobrights 65 lb"},
    {v:V_HEAVY,note:"80–110 lb cover / index"},
    {v:V_PREM_CARD,note:"Southworth cotton linen / parchment coverstock"},
    {v:V_COLORED,note:"colored cardstock"},
    {v:"Other cardstock",note:"a cardstock the title names but no rule places"}],
  "Ink & Toner":[
    {v:V_MAINT,note:"printer maintenance cartridges / boxes"},
    {v:"Other ink & toner",note:"ink or toner the title doesn't name a cartridge family for"}],
  "Business Cards":[
    {v:"Standard",note:"standard paper business cards"},
    {v:"Plastic",note:"plastic business cards"},
    {v:"Cotton",note:"cotton business cards"},
    {v:"Linen",note:"linen business cards"},
    {v:"Print-at-home",note:"blank cards you print yourself (Avery Clean Edge)"},
    {v:"Other business cards",note:"other card stocks (kraft, pearl…)"}],
  "Labels":[
    {v:"Address labels",note:"address / return-address labels (Avery 5160 and the like)"},
    {v:"Shipping labels",note:"shipping labels (4 x 6, half-sheet)"},
    {v:"Other labels",note:"other labels"}],
  // Postage kinds (10/11/26, point-in-time cost per letter): which postage
  // is a LETTER's stamp. Add-on stamps and parcels never are — not in the
  // stamp check, not in the point-in-time stamps. A part with no kind (every
  // line written before this) reads as before: letter stamps by $ ÷ price.
  "Postage":[
    {v:"Letter stamps",note:"forever / first-class stamps; one per letter"},
    {v:"Add-on stamps (not a letter)",note:"extra-ounce / top-up / low-denomination stamps (unit price below the letter rate)"},
    {v:"Parcel & shipping (not a letter)",note:"Priority Mail, labels, parcels on a postage receipt"},
    {v:"Postage balance (Stamps.com)",note:"money put on a Stamps.com balance; letter stamps at most balance ÷ 78¢"},
    {v:"Mailhouse (all-in)",note:"a mailhouse invoice: postage + printing for its drop"}]
};
var POSTAGE_KINDS={LETTER:"Letter stamps",ADDON:"Add-on stamps (not a letter)",PARCEL:"Parcel & shipping (not a letter)",BALANCE:"Postage balance (Stamps.com)",MAILHOUSE:"Mailhouse (all-in)"};
var VARIETY_RANK={};var VARIETY_NOTE={};
Object.keys(VARIETIES).forEach(function(t){VARIETY_RANK[t]={};VARIETIES[t].forEach(function(x,i){VARIETY_RANK[t][x.v]=i;VARIETY_NOTE[t+"|"+x.v]=x.note;});});
function hasVarieties(type){return!!(type&&VARIETIES[type]);}
function otherVariety(type){var l=VARIETIES[type];return l?l.filter(function(x){return/^Other /.test(x.v);}).map(function(x){return x.v;})[0]||null:null;}
function isOtherVariety(v){return/^Other /.test(String(v||""));}
// Sort position within a type: its list order; a family name built from the
// title (an ink cartridge) after the fixed ones, by name; "Other …" next to
// last; none last.
function varietyRank(type,v){
  if(!v)return 999;
  var r=VARIETY_RANK[type];
  if(isOtherVariety(v))return 900;
  if(r&&r[v]!==undefined)return r[v];
  return 500;
}
function varietyNote(type,v){
  if(!v)return"";
  var n=VARIETY_NOTE[type+"|"+v];if(n)return n;
  if(type==="Ink & Toner")return"ink / toner of this cartridge family"+(/\(compatible\)$/.test(v)?" — compatible (not the printer maker's own)":"");
  return"";
}
// Weight in lb from a cleaned title: "24 lb", "65lb", "32 lbs.", "28#",
// Staples' "32L" / "30% REC 32" — never "#10", "500#10" or a size.
function weightOf(s){
  var ok=function(n){n=+n;return n>=16&&n<=140?n:null;};
  var m=/\b(\d{2,3}) ?-? ?(?:lbs?|pounds?|#)\.?(?![a-z0-9])/.exec(s);if(m&&ok(m[1]))return ok(m[1]);
  m=/\b(\d{2}) ?l\b(?! ?x)/.exec(s);if(m&&ok(m[1]))return ok(m[1]);
  m=/\brec(?:ycled)? (\d{2})\b/.exec(s);if(m&&ok(m[1]))return ok(m[1]);
  return null;
}
function gsmOf(s){var m=/\b(\d{2,3}) ?(?:gsm|g\/m2)\b/.exec(s);return m?+m[1]:null;}
// A color in the title (never white / ivory / natural / cream — those are
// the plain shades — nor the brand names "Blue Summit", "TRU RED").
var COLOR_RE=/\b(navy|blue|red|green|pink|purple|violet|orange|yellow|gold|golden|silver|metallic|kraft|manila|brown|black|gr[ae]y|teal|pastel|dusty|lavender|mint|coral|burgundy|maroon|taupe|colou?red|colou?rs|assorted|rainbow|neon|fluorescent)\b/;
function colorIn(s){return COLOR_RE.test(s.replace(/blue summit|tru ?red|black ?&|& ?black|wordsworth/g," "));}
// ── Brand: Southworth or not (10/10/26, later) ───────────────
// Reece: "Realistically, for the paper types, you can just break it down to
// Southworth paper versus non-Southworth paper." Only Paper, Envelopes and
// Cardstock have one. A part's brand is SOUTHWORTH, NON_SOUTHWORTH (its
// line NAMES another brand — `otherBrandOf` — or the Amazon report's Brand
// column does) or null ("No brand detail": a hand-entered row that doesn't
// say, a bare "PAPER" receipt line, a store's "Copy paper" — never guessed;
// Staples' FINE LINEN / RESUME PPR / EXCEPT RESUME ENV / COVER short names
// are Southworth since 10/11/26, Reece's word).
var SOUTHWORTH="Southworth",NON_SOUTHWORTH="Non-Southworth",NO_BRAND_DETAIL="No brand detail";
var BRAND_TYPES={"Paper":true,"Envelopes":true,"Cardstock":true};
var BRAND_ORDER=[SOUTHWORTH,NON_SOUTHWORTH,NO_BRAND_DETAIL];
function hasBrand(type){return!!(type&&BRAND_TYPES[type]);}
// Southworth in a cleaned title: the name (also "Southworth®", which some
// exports print as "Southworth174;"; never a bare "South" — "Target Chandler
// South"), Staples' receipt abbreviation "S/W" ("S/W BARE WHITE 32L"), or one
// of Southworth's own item numbers as his titles and the Amazon report print
// them (with or without the "SOU" catalog prefix): JD18C, 404C / 404NC,
// J568C, RD18CF / RD18ACFLN / RD18BCFLN, R14ICF, R1410L / R14-10L, Z550CK,
// Z560CK, Z980CK, 964C / 974C / P964CK / P974CK / P984CK, J404I10 / J404N10
// (J404N-10), J55410 / J56410 (J564-10). And (10/11/26 — Reece: "yes, all of
// those are southworth styles") a Staples line whose UPC starts 083514,
// Southworth's prefix (STAPLES_SW_UPC_RE in a text; none of his titles
// carries one — a line's own UPC comes from the receipt reader's `upc` or an
// Itemized Details UPC column: upcIsSouthworth, review RV-4),
// and the short names Staples' receipts print for those UPCs
// (STAPLES_SW_SHORT_RE, at the START of the item title): FINE LINEN (WHITE /
// IVORY / ENV …), FINE PARCH, RESUME PPR, RESUME 8.5X11 LINE…, EXCEPT RESUME
// ENV, COVER 8.5X11 WHT / IVRY. Never a bare "South", and never "fine linen"
// inside another brand's title ("Neenah … Fine Linen").
var SOUTHWORTH_RE=/southworth|\bs\/w\b|\b(?:sou)?(?:jd18c|404n?c|j568c|rd18a?b?cf(?:ln)?|r14i?cf|r14-?10l|z5[56]0ck|z980ck|p?9[678]4ck?|j404[in]-?10|j5[56]4-?10)\b/;
var STAPLES_SW_UPC_RE=/\b083514 ?\d{5,6}\b/;
var STAPLES_SW_SHORT_RE=/^(?:fine linen (?:env|white|wht|ivory|ivo|ivr|natural|nat|cream|gray|grey)\b|fine parch (?:ppr|paper|env|ivr|ivory|ivo|white|wht)\b|resume ppr\b|resume 8\.5 ?x ?11 lin(?:e|en)?\b|except resume env\b|cover 8\.5 ?x ?11 (?:wht|white|ivry|ivory|ivr|ivo)\b)/;
// The item title's start: after an email's "Item:" / "Ordered: 2 "…", an
// imported row's "Staples — " and a receipt's "Staples #1395 - ".
function staplesShortName(s){
  var t=titleLead(s);var i=t.indexOf(" — ");
  var heads=[t];if(i>0)heads.push(titleLead(t.slice(i+3)));
  return heads.some(function(h){return STAPLES_SW_SHORT_RE.test(h.replace(/^staples(?: store)?(?: ?#? ?\d+)?\s*[-–:]\s*/,""));});
}
function isSouthworthText(s){
  s=String(s).replace(/\b(non|not)[- ]?southworth\b/g," ");
  return SOUTHWORTH_RE.test(s)||STAPLES_SW_UPC_RE.test(s)||staplesShortName(s.toLowerCase());
}
// An item line's own UPC (the receipt reader's optional `upc`, an Itemized
// Details UPC / SKU column — review RV-4: a UPC only in the text was the
// only way, and no receipt or export put one there): Southworth's 083514…
// (a 13-digit EAN's leading 0 too).
function upcIsSouthworth(upc){var u=String(upc==null?"":upc).replace(/[\s-]/g,"");return/^0?083514\d{5,6}$/.test(u);}
// An item title (or a description) → "Southworth" or null.
function brandOf(title){
  var s=cleanText(title);
  return s.trim()&&isSouthworthText(s)?SOUTHWORTH:null;
}
// Another brand NAMED by an item title (10/10/26 review — a line whose kind
// can be read but which names no brand used to be Non-Southworth: Staples'
// receipt short names FINE LINEN, FINE PARCH, RESUME PPR, RESUME 8.5X11
// LINE, EXCEPT RESUME ENV, FINE LINEN ENV and COVER 8.5X11 name none — the
// UPCs under them on his receipts are 083514…, and Reece confirmed 10/11/26
// they are Southworth: isSouthworthText). Named = the title STARTS with a
// brand from his real titles or a well-known paper / envelope maker (after an
// email's "Ordered: 2 "…" / "Item:"), or is one of Staples' OWN short names
// (UPC 718103… on his receipts, the prefix of STAPLES 65LB WHITE / STAPLES
// 30% REC 32): ENV #10 P&S SECURI, ENVELOPE SECURITY, ENVELOPE GUM #10, ENV
// PULL & SEAL SE. "Staples" is the brand only before a weight or a product
// word ("STAPLES 65LB", "Staples Laser Paper") — "Staples #1395 - …" is the
// store, like "Target delivery via Uber - …" / "Office Depot delivery …"
// ("Office Depot® Brand" is the brand).
var OTHER_BRAND_RE=/^(?:amazon ?basics|hammermill|tru ?red|astro ?brights|astro ?designs|neenah|aimoh|blue summit|columbian|quality park|jam paper|lux ?paper|office ?depot brand|up ?& ?up|boise|navigator|georgia[- ]pacific|xerox|domtar|pen ?\+ ?gear|mead|mohawk|wausau|hp (?:printer|copy|office|premium|multi-?purpose|everyday|laser|bright|paper)|staples (?:\d{2,3} ?(?:%|lbs?\b|#|-)|laser|copy|multi-?purpose|card ?stock|premium|brand|bright|recycled|security|#\s?10(?!\d)|envelopes?|r[eé]sum[eé]|ivory|white))(?![a-z])|^(?:env #10 p ?& ?s securi|envelope security|envelope gum #10|env pull ?& ?seal se)\b/;
function titleLead(s){
  return s.trim().replace(/^(?:item|ordered|order|shipped|delivered|advance refund issued for|refund issued for|refund on)\s*:?\s*/,"").replace(/^\d+\s*x?\s*(?=["“”'])/,"").replace(/^["“”']+\s*/,"");
}
// (also after an imported row's own "Amazon — <title>" / "Office Depot (Uber
// delivery) — <title>")
function otherBrandOf(title){
  var s=cleanText(title);
  if(!s.trim()||isSouthworthText(s))return false;
  var i=s.indexOf(" — ");
  return OTHER_BRAND_RE.test(titleLead(s))||(i>0&&OTHER_BRAND_RE.test(titleLead(s.slice(i+3))));
}
// The Amazon report's Brand column → SOUTHWORTH, NON_SOUTHWORTH (it names
// another brand — "Hammermill", "Amazon Basics", "Topeen"…) or undefined
// (empty, "Generic", "Unbranded", "N/A" — nothing named).
function columnBrand(v){
  var s=oneLine(v).replace(/^=?"(.*)"$/,"$1").trim();
  if(!s||!/[a-z]/i.test(s)||/^(?:n\/?a|none|null|unknown|unbranded|generic|no ?brand|not applicable|brand)$/i.test(s))return undefined;
  return brandOf(s)?SOUTHWORTH:NON_SOUTHWORTH;
}
// A brand as typed / given → SOUTHWORTH, NON_SOUTHWORTH, null ("No brand
// detail", "none", "") — or undefined when the text isn't a brand at all.
function brandToken(text){
  var l=oneLine(text).toLowerCase().replace(/^\[|\]$/g,"").trim();
  if(!l||l===NO_BRAND_DETAIL.toLowerCase()||l==="no brand"||l==="none")return null;
  if(/^(non|not)[- ]?(southworth|s\/w)$|^other brands?$/.test(l))return NON_SOUTHWORTH;
  if(/^(southworth|s\/w)$/.test(l))return SOUTHWORTH;
  return undefined;
}
function canonicalBrand(text){var b=brandToken(text);return b===undefined?null:b;}
// The explorer's / table's key for a part's brand.
function brandKeyOf(p){return(p&&hasBrand(p.type)&&p.brand)||NO_BRAND_DETAIL;}
function brandRank(b){var i=BRAND_ORDER.indexOf(b||NO_BRAND_DETAIL);return i<0?BRAND_ORDER.length:i;}
var BRAND_NOTE={};
BRAND_NOTE[SOUTHWORTH]="Southworth by name, Staples' S/W, a Southworth item number (JD18C, J404N10, Z550CK…), or Staples' Southworth short names as the receipt prints them (FINE LINEN, FINE PARCH, RESUME PPR, EXCEPT RESUME ENV, COVER 8.5X11) or a receipt line's UPC 083514…";
BRAND_NOTE[NON_SOUTHWORTH]="every other brand — the line (or Amazon's Brand column) names one that isn't Southworth (Amazon Basics, Hammermill, Staples' own, LUXPaper…)";
BRAND_NOTE[NO_BRAND_DETAIL]="purchases whose lines don't name a brand (entered by hand without one, a bare PAPER line, a store's \"copy paper\") — never guessed";
function brandNote(b){return BRAND_NOTE[b||NO_BRAND_DETAIL]||"";}

function paperVariety(s){
  if(/parch|granite|specialty|\b[pz]?9[6-9]4 ?ck?\b/.test(s))return V_PARCH;
  if(/\blinen\b|\blin\b| line $/.test(s))return V_LINEN;            // Staples prints "RESUME 8.5X11 LINE…"
  if(/\d+ ?% ?cotton|\bcotton\b|\brag\b/.test(s))return V_COTTON;
  var w=weightOf(s),g=w?null:gsmOf(s);
  if(w&&w<60)return w>=29?V_32:w>=26?V_28:V_STD_COPY;
  if(g&&g<150)return g>=113?V_32:g>=100?V_28:V_STD_COPY;
  if(/r[eé]sum[eé]|\bresume\b/.test(s))return V_32;
  // Southworth's paper is cotton paper — also when the title only says
  // "Premium Copy & Multipurpose" (J568C is 25% cotton linen business paper,
  // priced like the 25% cotton boxes), so the brand comes before the
  // copy / multipurpose words. A weight on the title still decides first.
  // (Southworth = the name, S/W or one of its item numbers — brandOf's rule.)
  if(isSouthworthText(s))return V_COTTON;
  if(/\bcopy\b|multi-?purpose|printer paper|printing paper|\blaser\b|\binkjet\b|office paper|\bbond\b|\breams?\b/.test(s))return V_STD_COPY;
  return null;
}
function envelopeVariety(s){
  if(/\bwindows?\b/.test(s)&&!/windowless|no ?windows?|non-?window|without (a )?window/.test(s))return V_WINDOW;
  if(/\b(6 ?x ?9|9 ?x ?12|10 ?x ?13|10 ?x ?15|12 ?x ?15|6 ?1\/2 ?x ?9 ?1\/2)\b|catalog|\bclasp\b|booklet|jumbo|\blarge\b|flat mailers?/.test(s))return V_LARGE;
  if(/#\s?9\b|\bno\.? ?9\b/.test(s))return"Other envelopes";
  if(/r[eé]sum[eé]|\bresume\b/.test(s))return V_RES_ENV;
  if(isSouthworthText(s)||/cotton|linen|parch|luxpaper|\bfine\b|\b\d{2,3} ?lbs?\.? ?text\b|\bwove\b|executive|stationery|\blaid\b|\bfelt\b/.test(s))return V_PREM_ENV;
  if(/secur|\btint|\bp ?& ?s\b|pull ?(&|and) ?seal|peel ?(&|and) ?(seal|stick|press)|press ?(&|and) ?seal|lift ?(&|and) ?press|self[- ]?seal/.test(s))return V_STD_ENV;
  if(/\bpremium\b/.test(s))return V_PREM_ENV;
  if(colorIn(s))return V_COLORED;
  if(/#\s?10\b|\bno\.? ?10\b|\bbusiness\b|\bletter\b|\bgum(med)?\b|\bmailing\b|\bplain\b|\bstandard\b/.test(s))return V_STD_ENV;
  return null;
}
function cardstockVariety(s){
  if(isSouthworthText(s)||/linen|parch|cotton|\bfelt\b|\blaid\b/.test(s))return V_PREM_CARD;
  if(colorIn(s))return V_COLORED;
  var w=weightOf(s),g=w?null:gsmOf(s);
  if((w&&w>=80)||(g&&g>=190))return V_HEAVY;
  if((w&&w>=60)||(g&&g>=150))return V_65;
  return null;
}
function inkVariety(s){
  var compat=(/compatible (for|with) (canon|hp|epson|brother)\b|\bfor (canon|hp|epson|brother)\b|replacement|remanufactured|\bgeneric\b|\(compatible\)/.test(s))&&!/\bgenuine\b|\boriginal\b|\boem\b/.test(s);
  var tag=function(l){return compat?l+" (compatible)":l;};
  var m;
  if(/maintenance (box|cartridge|kit|tank)|\bmc-?g ?\d+/.test(s))return V_MAINT;
  if((m=/\bgi[- ]?(\d{2,3})\b/.exec(s)))return tag("Canon GI-"+m[1]);
  if((m=/\b(pg|cl)[- ]?(\d{2,3})/.exec(s)))return tag("Canon "+m[1].toUpperCase()+"-"+m[2]);
  if((m=/\btoner(?: cartridge)? (\d{2,4}[a-z]?)\b|\b(\d{2,4}[a-z]?) (?:(?:standard|high|super high)[- ]yield )?toner\b/.exec(s))){
    var n=(m[1]||m[2]).toUpperCase();var b=/canon/.test(s)?"Canon":/\bhp\b/.test(s)?"HP":/brother/.test(s)?"Brother":"";
    return tag((b?b+" toner ":"Toner ")+n);
  }
  if((m=/\btn-? ?(\d{3,4})/.exec(s)))return tag("Brother TN-"+m[1]);
  if((m=/\bhp ?(\d{2,3})(?:[a-z]{0,2})\b/.exec(s)))return tag("HP "+m[1]);
  return null;
}
function businessCardVariety(s){
  if(/plastic/.test(s))return"Plastic";
  if(/cotton/.test(s))return"Cotton";
  if(/linen/.test(s))return"Linen";
  if(/avery|clean edge|printable|\binkjet\b|\blaser\b|print-at-home|print at home/.test(s))return"Print-at-home";
  if(/\b(kraft|pearl|metallic|recycled|soft touch|suede)\b/.test(s))return"Other business cards";
  if(/\bstandard\b|\bmatte\b|\bglossy\b|\bclassic\b|\buncoated\b/.test(s))return"Standard";
  return null;
}
function labelVariety(s){
  if(/address|return|\b5160\b|\b5260\b|\b5195\b|\b8160\b|30 (labels? )?(per|\/) ?sheet|1" ?x ?2/.test(s))return"Address labels";
  if(/shipping|4" ?x ?6|\b4 ?x ?6\b|\b5163\b|\b5168\b|\b8163\b|half[- ]sheet|2 (labels? )?(per|\/) ?sheet/.test(s))return"Shipping labels";
  return null;
}
// The kind of postage a title is (first match wins): the mailhouse, a
// Stamps.com / meter balance, parcels and shipping, add-on denominations,
// then letter stamps — else none. A receipt LINE's letter / add-on split is
// decided by its price (stampLineFacts), never by its title.
function postageVariety(s){
  if(MAILHOUSE_RE.test(s))return POSTAGE_KINDS.MAILHOUSE;
  if(/stamps\.com|postage balance|\bmeter\b/.test(s))return POSTAGE_KINDS.BALANCE;
  if(/priority mail|\bpm express\b|\bparcels?\b|click-?n-?ship|ground advantage|shipping label|certified|tracking/.test(s))return POSTAGE_KINDS.PARCEL;
  if(/additional ounce|non-?machinable|\badd-?ons?\b|\b(1|2|3|5|10|29|61) ?¢/.test(s))return POSTAGE_KINDS.ADDON;
  if(/\bstamps?\b|\bcoils?\b|\bbook(let)?s?\b|sheet of|pane of|forever/.test(s))return POSTAGE_KINDS.LETTER;
  return null;
}
var VARIETY_RULES={"Paper":paperVariety,"Envelopes":envelopeVariety,"Cardstock":cardstockVariety,"Ink & Toner":inkVariety,"Business Cards":businessCardVariety,"Labels":labelVariety,"Postage":postageVariety};
// The letter rate on a day (USPS / stores: 78¢ before 7/13/2026, 82¢ from
// then — his receipts' "US Flag Coil/100 $78.00" → "$82.00").
function letterRateOn(day){return String(day||"")<"2026-07-13"?78:82;}
// One postage receipt LINE (qty × unitCents on `day`) → {kind, stamps}:
// letter stamps (the stamps a letter can go out on — a coil / book / sheet
// at the letter rate per stamp, a book bought as one price that divides by
// the rate, an over-rate single) or an add-on denomination (below the rate:
// 29¢ School Bus, 61¢, 2¢ — never a letter; no stamp count).
function stampLineFacts(desc,qty,unitCents,day){
  var q=Math.max(1,Math.round(Number(qty)||1));var u=Math.round(Number(unitCents)||0);
  var rate=letterRateOn(day);
  var s=String(desc||"");
  var m=/(?:coil|book(?:let)?|bklt|sheet|pane|roll)\s*(?:of|\/)\s*(\d[\d,]*)/i.exec(s)||/[a-z]\s*\/\s*(\d{1,4})\b/i.exec(s);
  var pack=m?countNum(m[1]):null;
  if(pack&&u>0){
    if(u/pack>=rate-1)return{kind:POSTAGE_KINDS.LETTER,stamps:q*pack};
    return{kind:POSTAGE_KINDS.ADDON,stamps:0};
  }
  if(u>=rate&&u%rate===0)return{kind:POSTAGE_KINDS.LETTER,stamps:q*u/rate};
  if(u>=rate)return{kind:POSTAGE_KINDS.LETTER,stamps:q};
  return{kind:POSTAGE_KINDS.ADDON,stamps:0};
}
// The words that say nothing beyond the type itself ("RESUME PPR" isn't
// one — résumé is a variety): what's left decides "Other <type>" vs none.
var GENERIC_WORDS_RE=/\b(papers?|ppr|sheets?|shts?|sh|reams?|rm|packs?|pk|pkg|box(es)?|bx|ct|cnt|count|qty|pcs|pieces|each|white|wht|ivory|ivry|ivr|natural|bright|brightness|letter|legal|size|inch(es)?|in|lbs?|envelopes?|envs?|env|card ?stock|cards?|stock|cover|inks?|toner|labels?|lbls?|business|black|blk|the|and|of|for|with|by|on|to|new|item|items|set|refill|bottles?|cartridges?|amazon|staples|office ?depot|target|walmart|vistaprint|purchase|order|receipt|supplies|misc)\b/g;
function namesAProduct(s){
  var rest=s.replace(GENERIC_WORDS_RE," ").replace(/[^a-z ]+/g," ");
  return/[a-z]{3,}/.test(rest);
}
// The rule-matched variety of a title for a type (null when no rule fits).
// Used for a hand-typed description ("28 lb paper", "Southworth
// envelopes") — never guesses "Other".
function namedVarietyOf(type,title){
  var f=VARIETY_RULES[type];if(!f)return null;
  var s=cleanText(title);if(!s.trim())return null;
  return f(s)||null;
}
// The variety of an item TITLE (an order / receipt line) of that type: the
// rule that fits, else "Other <type>" when the title names a product, else
// null (nothing beyond the type).
function varietyOf(type,title){
  var v=namedVarietyOf(type,title);if(v)return v;
  if(!VARIETY_RULES[type])return null;
  return namesAProduct(cleanText(title))?otherVariety(type):null;
}
// A variety as typed ("cotton", "Standard #10", "Canon GI-26") → its label;
// a short form (the label before its "(…)") or anything a rule reads; else
// kept as typed. "No type detail" = none.
function canonicalVariety(type,text){
  var t=oneLine(text).replace(/^\[|\]$/g,"").trim();
  if(!t)return null;
  var l=t.toLowerCase();
  if(l===NO_TYPE_DETAIL.toLowerCase()||l==="none")return null;
  var list=VARIETIES[type];
  if(!list)return t;
  for(var i=0;i<list.length;i++)if(list[i].v.toLowerCase()===l)return list[i].v;
  for(var j=0;j<list.length;j++)if(list[j].v.toLowerCase().split(" (")[0]===l)return list[j].v;
  return namedVarietyOf(type,t)||t;
}

// ── Pack counts ──────────────────────────────────────────────

function countNum(s){var n=parseInt(String(s).replace(/,/g,""),10);return isFinite(n)&&n>0&&n<=1000000?n:null;}
// A number ENDING a title is a count only when it isn't a year ("Stamps —
// Sept 2026", "Envelopes 2025" — a round 2000 / 2050 still counts) nor sits
// after a month, an order / invoice / receipt / PO / account number marker,
// "#" or "no." (10/11/26 — a hand-typed "Stamps — Sept 2026" read as 2,026
// receipt-counted stamps). head = the text before the number.
var NOT_COUNT_HEAD_RE=/(?:\b(?:jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|june?|july?|aug(?:ust)?|sept?(?:ember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?|orders?|ord|invoices?|inv|receipts?|rcpt|confirmation|conf|ref|reference|po|acct|account|number|num|no|sku|model|batch)\.?|#)\s*[:#.-]?\s*$/;
function yearLike(n){return n>=1990&&n<=2099&&n%50!==0;}
function trailingCountOk(head,n){return!!n&&!yearLike(n)&&!NOT_COUNT_HEAD_RE.test(String(head||""));}
// The words that count units of each kind ("500 count", "250 ct" count
// anything). A sheet count is NOT a label count: Avery's "30 Labels per
// Sheet, 25 Sheets" is 750 labels, and "25 Sheets" alone is unknown.
var COUNT_W="count|ct|cnt|qty|pcs|pieces|pc";
var UNIT_W={sheets:"sheets?|shts?|sh",envelopes:"envelopes?|envs?",labels:"labels?|lbls?",stamps:"stamps?",cards:"cards?"};
function unitWordsFor(unit){
  var w=UNIT_W[unit]?[UNIT_W[unit]]:[UNIT_W.sheets,UNIT_W.envelopes,UNIT_W.labels,UNIT_W.stamps];   // (cards only for Business Cards)
  return w.concat([COUNT_W]).join("|");
}
var CONTAINERS="packs?|pk|pkg|box(?:es)?|bx|cases?|cs|cartons?|ctn|bundles?|reams?|rm|rolls?|books?|coils?";
// Units in ONE of the thing bought (as bought — "500 Sheets/Ream, 10 Reams/
// Case" is 5,000), in `unitName` (sheets / envelopes / labels / stamps;
// default: the title's own item type's) → number or null. Never a guess:
//  • an explicit total wins — "(5000 Sheets)", "/1500 Sheets", "(3,000 labels)";
//  • "6 Packs of 250 Sheets" = 1,500;
//  • a per-container count ("500/Box", "500 Sheets per Ream", "Box of 500")
//    times how many containers ("2 Boxes", "10 Reams/Case", "Pack of 2");
//  • a bare count ("500 count", "250 Sheets", Blue Summit "500#10", "500
//    Pack") on its own; beside a container count it is only taken when it
//    IS the total ("10 Reams, 5000 Sheets") — otherwise unknown;
//  • "Ream" = 500 sheets; labels: "N labels" / "N ct", or labels per sheet ×
//    sheets — never a sheet count alone;
//  • two different counts that don't fit together → null.
// Weights, sizes, brightness, "#10" and bare numbers are never counts.
function packCountOf(text,unitName){
  var s=cleanText(text);var m;
  if(!s.trim())return null;
  var unit=unitName!==undefined&&unitName!==null?unitName:unitNameOf(itemTypeOf(text));
  var W=unitWordsFor(unit);
  // Sizes are never counts: Blue Summit's "500#10" is 500 envelopes, but the
  // 10 in "#10 Envelopes" / "No. 10" and the 2 in "9 1/2 Envelopes" are not.
  s=s.replace(/\b(\d{2,}) ?# ?10\b/g,unit==="envelopes"||!unit?"$1 envelopes ":"$1 ")
    .replace(/#\s?\d+/g," ").replace(/\b(?:no\.?|number|size) ?\d+\b/g," ").replace(/\b\d+ ?\/ ?\d+\b/g," ");
  var re=function(src){return new RegExp(src,"g");};
  var all=function(r){var out=[];var x;r.lastIndex=0;while((x=r.exec(s)))out.push(x);return out;};
  // 1. An explicit total: in parentheses, or after a slash.
  if((m=re("[(/]\\s*(\\d[\\d,]*) ?(?:"+W+")\\b(?!\\s*(?:each|per\\b|\\/|a\\b|an\\b))").exec(s))){var t=countNum(m[1]);if(t)return t;}
  // 2. "6 Packs of 250 Sheets", "5 Books of 20".
  if((m=/\b(\d{1,3}) ?(?:packs?|boxes|reams?|pk|cases?|cartons?|books?|rolls?|coils?) of (\d[\d,]*)(?: ?([a-z]+))?/.exec(s))){
    var a=countNum(m[1]),b=countNum(m[2]);
    if(a&&b&&(!m[3]||re("^(?:"+W+")$").test(m[3])||!/^(sheets?|shts?|envelopes?|envs?|labels?|lbls?|stamps?)$/.test(m[3])))return a*b;
    return null;
  }
  if(unit==="labels"){
    // "3,000 Labels", "250 ct" — not "30 Labels per Sheet".
    var lab=all(re("\\b(\\d[\\d,]*) ?(?:labels?|lbls?|"+COUNT_W+")\\b(?!\\s*(?:per|\\/))"));
    var lv=uniqNums(lab.map(function(x){return countNum(x[1]);}));
    if(lv.length===1)return lv[0];
    if(lv.length>1)return null;
    var per=/\b(\d{1,3}) ?(?:labels? )?(?:per|\/) ?sheet\b/.exec(s);
    var sh=all(/\b(\d[\d,]*) ?(?:sheets?|shts?)\b(?!\s*(?:per|\/))/g);
    var sv=uniqNums(sh.map(function(x){return countNum(x[1]);}));
    if(per&&sv.length===1){var pp=countNum(per[1]);return pp?pp*sv[0]:null;}
    return null;
  }
  // 3. Every count in the title, by what it counts.
  var perC=[];   // units in ONE container: "500/Box", "500 Sheets per Ream", "Box of 500"
  var bare=[];   // units with no container said: "500 count", "250 Sheets", "500#10"
  var mult=[];   // how many containers: "2 Boxes", "10 Reams/Case", "Pack of 2"
  var nPack=[];  // "500 Pack" / "2-Pack": units on their own, containers beside a count
  all(re("\\b(\\d[\\d,]*) ?(?:"+W+")\\b(?! ?(?:per|\\/) ?roll)(\\s*(?:(?:per|\\/)\\s*(?:"+CONTAINERS+"|sheet)\\b|each\\b))?")).forEach(function(x){(x[2]?perC:bare).push(countNum(x[1]));});
  all(/\b(\d[\d,]*) ?\/ ?(?:box|bx|pack|pk|pkg|ream|rm|case|cs|carton|ctn|bundle|roll|book)\b/g).forEach(function(x){perC.push(countNum(x[1]));});
  // ("Box Of500" — Office Depot's title has no space after "Of", 10/11/26)
  var cof=all(/\b(?:pack|box|case|carton|set|bundle|roll|book|coil|pad|ream) of ?(\d[\d,]*)\b/g).map(function(x){return countNum(x[1]);});
  all(/\b(\d{1,3}) ?(?:reams|boxes|bx|packs|cases|cartons|bundles)\b/g).forEach(function(x){mult.push(countNum(x[1]));});
  all(/\b(\d{1,3}) ?reams?\b(?! of)/g).forEach(function(x){mult.push(countNum(x[1]));});
  all(/\b(\d[\d,]*) ?-? ?(?:pack|pk|pkg)\b(?! of)/g).forEach(function(x){nPack.push(countNum(x[1]));});
  var one=function(n){return n!==1;};   // "1 Box" multiplies nothing
  perC=uniqNums(perC);bare=uniqNums(bare);mult=uniqNums(mult).filter(one);nPack=uniqNums(nPack);cof=uniqNums(cof);
  // "Box of 500, Pack of 2": the biggest "… of N" is the units in a box, a
  // small one beside it is how many boxes. A lone "Pack of 2" counts boxes,
  // not envelopes — unknown.
  if(cof.length){
    cof.sort(function(x,y){return y-x;});
    if(!perC.length&&!bare.length){
      if(cof[0]<10)return null;
      perC.push(cof[0]);cof=cof.slice(1);
    }
    cof.forEach(function(n){if(perC.indexOf(n)>=0||bare.indexOf(n)>=0||n===1)return;mult.push(n<=50?n:-1);});
  }
  if(perC.length&&bare.length){
    // "500 Sheets/Ream, 10 Reams … 5000 Sheets": the bare one is the
    // total when it fits; the same number twice is one count.
    if(perC.length===1&&bare.length===1&&mult.length===1&&perC[0]*mult[0]===bare[0])return bare[0];
    if(perC.length===1&&bare.length===1&&perC[0]===bare[0])bare=[];
    else return null;
  }
  var units=perC.length?perC:bare;
  // A store receipt's short name with the count at the end (10/11/26 —
  // Staples' "RESUME PPR WHT 100" is a 100-sheet pack): a PPR / ENV line
  // ending in a 2–4 digit number that isn't a paper weight.
  if(!units.length&&!mult.length&&!nPack.length&&!cof.length&&(unit==="sheets"||unit==="envelopes"||!unit)){
    var tail=/\b(ppr|env|envs)\b.*?\b(\d{2,4})\s*$/.exec(s);
    if(tail&&!/\b\d{2,4} ?(lbs?|#|l)\s*$/.test(s)){var tn=countNum(tail[2]);if(tn&&tn>=25&&[28,32].indexOf(tn)<0&&trailingCountOk(s.slice(0,s.lastIndexOf(tail[2])),tn))return tn;}
  }
  if(!units.length){
    // "500 Pack" alone is 500 envelopes; a "2 Pack" alone is 2 boxes — unknown.
    if(nPack.length===1&&!mult.length)return nPack[0]<10?null:nPack[0];
    if(nPack.length)return null;
    var rm=/\b(?:(\d{1,3}) )?reams?\b/.exec(s);
    if(rm&&(unit==="sheets"||!unit)){var r=rm[1]?countNum(rm[1]):1;return r?500*r:null;}
    return null;
  }
  if(units.length>1)return null;
  var u=units[0];if(!u)return null;
  nPack.forEach(function(n){if(n!==u&&n!==1)mult.push(n<=50?n:-1);});
  mult=uniqNums(mult);
  if(!mult.length)return u;
  if(mult.length>1||mult[0]<0)return null;
  if(perC.length)return u*mult[0];
  // A bare count beside a container count: the total only when it is
  // exactly that many reams ("10 Reams, 5000 Sheets") — else unknown.
  return/\breams?\b/.test(s)&&u===500*mult[0]?u:null;
}
function uniqNums(a){var out=[];(a||[]).forEach(function(n){if(n!=null&&out.indexOf(n)<0)out.push(n);});return out;}

// ── Vendors ──────────────────────────────────────────────────

var VENDOR_GROUPS=[
  [/amazon|amzn/i,"Amazon"],[/staples/i,"Staples"],[/office ?depot|office ?max|\bodp\b/i,"Office Depot"],
  [/stamps\.com/i,"Stamps.com"],[/\busps\b|postal service|post office/i,"USPS"],
  [/wal-?mart/i,"Walmart"],[/costco/i,"Costco"],[/sam'?s club/i,"Sam's Club"],[/\btarget\b/i,"Target"],
  [/fedex/i,"FedEx"],[/\bups store\b/i,"The UPS Store"],[/best ?buy/i,"Best Buy"],[/home ?depot/i,"Home Depot"],[/michaels/i,"Michaels"],[/\bfry['’]?s\b|frysfood/i,"Fry's"],
  [/alpha ?graphics/i,"AlphaGraphics"],[/vistaprint/i,"Vistaprint"],[/property ?shark/i,"PropertyShark"],[/datazapp/i,"DataZapp"],
  [/prop ?stream/i,"PropStream"],[/prop ?radar/i,"PropRadar"]
];
// Stores sell everything — a receipt from one that doesn't list the items
// is "No item detail", never guessed from the store's name.
var STORE_GROUPS={"Amazon":true,"Staples":true,"Office Depot":true,"Walmart":true,"Costco":true,"Sam's Club":true,"Target":true,"FedEx":true,"The UPS Store":true,"Best Buy":true,"Home Depot":true,"Michaels":true,"Fry's":true};
function isStoreGroup(group){return!!STORE_GROUPS[group];}
function oneLine(s){return String(s==null?"":s).replace(/\s+/g," ").trim();}
// A vendor name → its group: Amazon / Amazon Business / AMAZON MKTPL →
// "Amazon", STAPLES 00123 → "Staples", …; anything else its own name with
// a store / terminal number taken off.
function vendorGroupOf(vendor){
  var s=oneLine(vendor);
  if(!s||/^unknown$/i.test(s))return NO_VENDOR;
  for(var i=0;i<VENDOR_GROUPS.length;i++)if(VENDOR_GROUPS[i][0].test(s))return VENDOR_GROUPS[i][1];
  var n=s.replace(/^(sq|tst|pp|paypal|sp)\s*\*\s*/i,"").replace(/\s*#\s*\d+.*$/,"").replace(/\s+\d{3,}\b.*$/,"").trim()||s;
  if(!/[a-z]/.test(n)&&/[A-Z]{3,}/.test(n))n=n.toLowerCase().replace(/(^|[\s\-/&.])[a-z]/g,function(c){return c.toUpperCase();});   // "JOE'S CAFE" → "Joe's Cafe"
  return n.length>40?n.slice(0,39)+"…":n;
}
// A known vendor at the START of a description ("Amazon - Envelopes &
// Paper", "STAPLES 00123") — for a row with no vendor of its own.
function knownVendorGroup(text){
  var s=oneLine(text).split(/\s[—–-]\s|:/)[0];
  for(var i=0;i<VENDOR_GROUPS.length;i++){var m=VENDOR_GROUPS[i][0].exec(s);if(m&&m.index===0)return VENDOR_GROUPS[i][1];}
  return null;
}
// The data websites he buys lists / reports from (Marketing → Data → one
// tile per site): named ANYWHERE in a vendor-less row's description
// ("Monthly PropStream", "Skip trace — BatchLeads"), not just at its start.
var DATA_SITES=[[/property ?shark/i,"PropertyShark"],[/prop ?stream/i,"PropStream"],[/prop ?radar/i,"PropRadar"],[/datazapp/i,"DataZapp"],
  [/batch ?(leads|data|skip)/i,"BatchLeads"],[/listsource/i,"ListSource"],[/melissa ?data/i,"Melissa Data"]];
function dataSiteIn(text){
  var s=oneLine(text);if(!s)return null;
  for(var i=0;i<DATA_SITES.length;i++)if(DATA_SITES[i][0].test(s))return DATA_SITES[i][1];
  return null;
}
function expenseVendorGroup(ex){
  var v=oneLine(ex&&ex.vendor);
  if(v&&!/^unknown$/i.test(v))return vendorGroupOf(v);
  return knownVendorGroup(ex&&ex.desc)||dataSiteIn(ex&&ex.desc)||NO_VENDOR;
}

// ── Items line ───────────────────────────────────────────────

function cents(n){return Math.round((Number(n)||0)*100);}
function fmtMoney(n){
  n=Number(n)||0;
  var s=Math.abs(n).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g,",");
  return(n<0&&Math.abs(n)>=0.005?"-":"")+"$"+s;
}
function fmtCount(n){var r=Math.round(n*100)/100;return(r%1?String(r):String(Math.round(r))).replace(/\B(?=(\d{3})+(?!\d))/g,",");}
// Split `totalC` cents in proportion to `weights` (cents) — the parts sum
// EXACTLY to the total, the odd pennies on the largest part.
function spread(totalC,weights){
  var sum=weights.reduce(function(s,w){return s+w;},0);
  if(!sum)return null;
  var parts=weights.map(function(w){return Math.round(totalC*w/sum);});
  var diff=totalC-parts.reduce(function(s,p){return s+p;},0);
  if(diff){var big=0;parts.forEach(function(p,i){if(Math.abs(p)>Math.abs(parts[big]))big=i;});parts[big]+=diff;}
  return parts;
}
// Postage is never taxed (10/10/26 — Fry's 4/17: "BK OF STAMPS 15.60",
// "HIGH NOON 25.99 T", TAX 2.16 = the High Noon's alone). Like spread(), but
// when the total is MORE than the lines (tax, fees, a tip) the postage in
// each weight (`postageW[i]`, cents) keeps exactly its printed price and only
// the rest of the weights share that extra. Under the lines (a discount), no
// postage, or nothing else to carry the extra → spread() as before. A
// negative total (a refund) works the same on its size.
function spreadUntaxedPostage(totalC,weights,postageW){
  var w=weights.map(function(x){return Math.max(0,x||0);});
  var pw=w.map(function(x,i){return Math.min(x,Math.max(0,(postageW&&postageW[i])||0));});
  var sum=w.reduce(function(s,x){return s+x;},0);
  var pSum=pw.reduce(function(s,x){return s+x;},0);
  var rest=w.map(function(x,i){return x-pw[i];});
  var abs=Math.abs(totalC);
  if(!sum||!pSum||pSum===sum||abs<=sum)return spread(totalC,weights);
  var add=spread(abs-sum,rest);
  if(!add)return spread(totalC,weights);
  var sign=totalC<0?-1:1;
  return w.map(function(x,i){return sign*(x+add[i]);});
}
// A receipt / order line that isn't an item: points, rewards, a coupon, a
// discount ("Buy 2 and get 1 at 100% off"). `specific` = about the line
// before it (folded into that line's type); otherwise it's spread over the
// whole row like the tax.
var ADJ_RE=/\bpoints?\b|\brewards?\b|redemption|coupon|discount|\bpromo|savings|% off|percent off|price adj|rebate|markdown|\bbogo\b|\bbuy \d+\b.*\bget\b|\bfree\b|loyalty|gift card|store credit/;
var ADJ_ITEM_RE=/% off|percent off|\bbuy \d+\b.*\bget\b|\bbogo\b|markdown|instant savings|item discount|\bfree\b/;
function numOrNull(v){if(v==null||v==="")return null;var n=Number(v);return isFinite(n)?n:null;}
// The variety of one item line of `type`: given (a ready file's / a parsed
// line's), else read from its title — an item TITLE ("Southworth J404I10 25%
// Cotton #10 …", "ENV #10 P&S SECURI") by varietyOf; a line whose type was
// given with it (a description / subject as one item) only by a rule.
function lineVariety(type,it){
  if(!hasVarieties(type))return null;
  if(it.variety!==undefined)return it.variety?canonicalVariety(type,it.variety):null;
  // (a Southworth UPC reads like a title that says Southworth — the kind rules' own test)
  var d=it.desc;if(upcIsSouthworth(it.upc)&&!brandOf(d))d="Southworth "+oneLine(d);
  return it.type?namedVarietyOf(type,d):varietyOf(type,d);
}
// The brand of one item line of `type` (Paper / Envelopes / Cardstock):
// given (a ready file's / a parsed line's — and an Amazon report line whose
// Brand column names one), else Southworth when its text says so, else
// Non-Southworth when its text NAMES another brand (`otherBrandOf`); null
// when no brand is named — a bare "PAPER" line, a store's "Copy paper", a
// kind given without a brand. (Until 10/10/26 review a kind read off the text
// was enough for Non-Southworth — a guess on Staples' short names; since
// 10/11/26 those short names — "FINE LINEN WHITE 3", "EXCEPT RESUME ENV" —
// are Southworth, Reece's word, and so is a line whose own `upc` is 083514….)
function lineBrand(type,it){
  if(!hasBrand(type))return null;
  if(it.brand!==undefined)return canonicalBrand(it.brand);
  if(brandOf(it.desc)||upcIsSouthworth(it.upc))return SOUTHWORTH;
  return it.variety===undefined&&otherBrandOf(it.desc)?NON_SOUTHWORTH:null;
}
// A type's lines group by kind (`v`: its weight decides the kind's dollars,
// exactly as before brands), and a kind's lines by brand (`b`: the leaf — its
// qty / units, and its weight within the kind).
function newVarGroup(variety){return{variety:variety,w:0,bs:new Map()};}
function newLeaf(brand,unitName){return{brand:brand,w:0,qty:0,qtyKnown:true,units:0,unitsKnown:true,unitName:unitName};}
function foldLeaves(leaves,unitName){
  var one=newLeaf(null,unitName);var brands={};
  leaves.forEach(function(b){one.w+=b.w;brands[b.brand||""]=1;if(!b.qtyKnown)one.qtyKnown=false;else one.qty+=b.qty;if(!b.unitsKnown)one.unitsKnown=false;else one.units+=b.units;});
  var ks=Object.keys(brands);one.brand=ks.length===1&&ks[0]?ks[0]:null;   // a brand they all share stays
  return one;
}
// Fold a type's groups into one — when its lines can't be split between
// kinds honestly (an unpriced line, nothing to weigh by): no kind (unless
// they are all one kind), and a brand only when every line has the same one.
function collapseVarieties(g){
  var vs=Array.from(g.vs.values());var leaves=[];var w=0;
  vs.forEach(function(v){w+=v.w;v.bs.forEach(function(b){leaves.push(b);});});
  var one=newVarGroup(vs.length===1?vs[0].variety:null);one.w=w;
  one.bs=new Map([["",foldLeaves(leaves,g.unitName)]]);
  g.vs=new Map([["",one]]);
}
// …and one kind's brands into one (its dollars can't be split between them).
function collapseBrands(v,unitName){v.bs=new Map([["",foldLeaves(Array.from(v.bs.values()),unitName)]]);}
function partOut(type,v,b,c){
  var p={type:type,variety:v.variety||null,cents:c,amount:c/100,qty:b.qtyKnown?b.qty:null,units:b.unitName&&b.unitsKnown&&b.units>0?b.units:null,unitName:b.unitName};
  if(b.brand)p.brand=b.brand;
  return p;
}
function byTypeThenVariety(a,b){return typeOrder(a.type)-typeOrder(b.type)||(a.type===b.type?varietyRank(a.type,a.variety)-varietyRank(b.type,b.variety)||String(a.variety||"").localeCompare(String(b.variety||""))||brandRank(a.brand)-brandRank(b.brand):0);}
// items [{desc, qty, amount, type?, variety?, brand?, noQty?}] + the row's
// amount → one part per item type AND variety (AND brand, for Paper /
// Envelopes / Cardstock): [{type, variety, brand?, cents, amount, qty,
// units, unitName}] summing EXACTLY to the row, in ITEM_TYPES order (a
// type's varieties in their own order, a kind's brands Southworth first).
// Three levels, so a variety never moves a type's dollars and a brand never
// moves a kind's: each TYPE gets exactly what it got before varieties
// existed (its lines + its share of the tax / shipping / promotions /
// points), then a type's dollars are split between its varieties in
// proportion to their lines, then a kind's between its brands the same way.
// `brand` is set only when there is one. [] when it can't be split honestly
// (several types and a line with no amount) or the row is $0.
function itemParts(items,rowAmount){
  var rowC=cents(rowAmount);
  var list=(items||[]).filter(function(it){return it&&(oneLine(it.desc)||it.type);});
  if(!list.length||!rowC)return[];
  var raw=list.map(function(it){var a=numOrNull(it.amount);return{it:it,c:a==null?null:Math.round(a*100)};});
  // A refund's lines may be written as negatives: they are still the items.
  var nz=raw.filter(function(x){return x.c;});
  if(nz.length&&nz.every(function(x){return x.c<0;}))raw.forEach(function(x){if(x.c!=null)x.c=-x.c;});
  var groups=new Map();var last=null;var anyNull=false;
  raw.forEach(function(x){
    var it=x.it;var d=cleanText(it.desc);
    var type=it.type?canonicalType(it.type):itemTypeOf(it.desc);
    var adj=(x.c!=null&&x.c<0)||(!type&&ADJ_RE.test(d)&&(x.c==null||x.c<=0));
    if(adj){if(x.c&&last&&ADJ_ITEM_RE.test(d)){last.w+=x.c;last.v.w+=x.c;last.v.g.w+=x.c;}return;}
    type=type||ITEM_OTHER;
    var g=groups.get(type);
    if(!g){g={type:type,w:0,vs:new Map(),unitName:unitNameOf(type)};groups.set(type,g);}
    var variety=lineVariety(type,it);var vk=variety||"";
    var v=g.vs.get(vk);
    if(!v){v=newVarGroup(variety);v.g=g;g.vs.set(vk,v);}
    var brand=lineBrand(type,it);var bk=brand||"";
    var b=v.bs.get(bk);
    if(!b){b=newLeaf(brand,g.unitName);b.v=v;v.bs.set(bk,b);}
    if(x.c==null){anyNull=true;g.anyNull=true;}else{g.w+=x.c;v.w+=x.c;b.w+=x.c;}
    var q=numOrNull(it.qty);
    if(q==null&&!it.noQty){var mm=/\b(\d+) more items?\b/.exec(d);q=mm?Number(mm[1]):1;}
    // A line can carry its count outright (10/11/26: a USPS receipt line's
    // letter stamps — stampLineFacts —, a sheet's units column): that many
    // units for the line, whatever its qty.
    var eu=numOrNull(it.units);if(eu!=null&&!(eu>0))eu=null;
    if(it.noQty||q==null){b.qtyKnown=false;if(eu!=null&&b.unitName)b.units+=eu;else b.unitsKnown=false;}
    else if(eu!=null&&b.unitName){b.qty+=q;b.units+=eu;}
    else{
      var per=b.unitName?packCountOf(it.desc,b.unitName):null;
      // Business cards are sold by the card: a receipt line "Standard
      // Business Cards" with quantity 1,000 and no count in its title IS
      // 1,000 cards (one purchase), not 1,000 boxes.
      if(per==null&&b.unitName==="cards"&&q>=50){per=q;q=1;}
      b.qty+=q;
      if(b.unitName){if(per!=null)b.units+=per*q;else if(q)b.unitsKnown=false;}
    }
    last=b;
  });
  var types=Array.from(groups.values());
  if(!types.length||(anyNull&&types.length>1))return[];
  var cs;
  if(types.length===1)cs=[rowC];
  else cs=spreadUntaxedPostage(rowC,types.map(function(g){return Math.max(0,g.w);}),types.map(function(g){return g.type==="Postage"?g.w:0;}));
  if(!cs)return[];
  var out=[];
  types.forEach(function(g,i){
    if(g.anyNull){if(g.vs.size>1)collapseVarieties(g);else g.vs.forEach(function(v){if(v.bs.size>1)collapseBrands(v,g.unitName);});}
    var vs=Array.from(g.vs.values());
    var vc=vs.length===1?[cs[i]]:spread(cs[i],vs.map(function(v){return Math.max(0,v.w);}));
    if(!vc){collapseVarieties(g);vs=Array.from(g.vs.values());vc=[cs[i]];}
    vs.forEach(function(v,k){
      var bs=Array.from(v.bs.values());
      var bc=bs.length===1?[vc[k]]:spread(vc[k],bs.map(function(b){return Math.max(0,b.w);}));
      if(!bc){collapseBrands(v,g.unitName);bs=Array.from(v.bs.values());bc=[vc[k]];}
      bs.forEach(function(b,j){out.push(partOut(g.type,v,b,bc[j]));});
    });
  });
  out.sort(byTypeThenVariety);
  return out;
}
// A receipt line that is a discount on the line before it ("Buy 2 and get 1
// at 100% off", "20% off") — the receipt splitter folds it into that line's
// category, like itemParts folds it into its type.
function itemDiscountLine(desc){return ADJ_ITEM_RE.test(cleanText(desc));}
function typeOrder(type){
  if(TYPE_INFO[type]&&type!==ITEM_OTHER&&type!==NO_DETAIL)return TYPE_INFO[type].order;
  if(NAMED[type])return TYPE_INFO.Software.order+0.5;
  return type===NO_DETAIL?999:998;
}
// Several sources for one expense (two Amazon orders behind one emailed
// charge) → one set of parts by type, variety and brand, re-split to
// `totalAmount` when the sources are a cent off it — by type first (as
// before varieties), then within each type by variety, then within each
// kind by brand, so the types' (and kinds') dollars don't move.
function mergeItemParts(lists,totalAmount){
  var by=new Map();
  (lists||[]).forEach(function(parts){(parts||[]).forEach(function(p){
    var t=by.get(p.type);
    if(!t){t={type:p.type,cents:0,vs:new Map()};by.set(p.type,t);}
    var vk=p.variety||"";var v=t.vs.get(vk);
    if(!v){v={variety:p.variety||null,cents:0,bs:new Map()};t.vs.set(vk,v);}
    var bk=p.brand||"";var g=v.bs.get(bk);
    if(!g){g={brand:p.brand||null,cents:0,qty:0,qtyKnown:true,units:0,unitsKnown:true,unitName:p.unitName};v.bs.set(bk,g);}
    t.cents+=p.cents;v.cents+=p.cents;g.cents+=p.cents;
    if(p.qty==null)g.qtyKnown=false;else g.qty+=p.qty;
    if(p.units==null)g.unitsKnown=false;else g.units+=p.units;
  });});
  var types=Array.from(by.values());
  if(!types.length)return[];
  var split=function(c,list){return list.length===1?[c]:spread(c,list.map(function(x){return x.cents;}));};
  if(totalAmount!=null){
    var tc=cents(totalAmount);var sum=types.reduce(function(s,t){return s+t.cents;},0);
    if(sum!==tc){
      var cs=spread(tc,types.map(function(t){return t.cents;}));if(!cs)return[];
      var ok=types.every(function(t,i){
        var vs=Array.from(t.vs.values());
        var vc=split(cs[i],vs);
        if(!vc)return false;
        var fine=vs.every(function(v,k){
          var bs=Array.from(v.bs.values());var bc=split(vc[k],bs);
          if(!bc)return false;
          bs.forEach(function(g,j){g.cents=bc[j];});v.cents=vc[k];
          return true;
        });
        t.cents=cs[i];
        return fine;
      });
      if(!ok)return[];
    }
  }
  var out=[];
  types.forEach(function(t){t.vs.forEach(function(v){v.bs.forEach(function(g){
    var p={type:t.type,variety:v.variety,cents:g.cents,amount:g.cents/100,qty:g.qtyKnown?g.qty:null,units:g.unitName&&g.unitsKnown&&g.units>0?g.units:null,unitName:g.unitName};
    if(g.brand)p.brand=g.brand;
    out.push(p);
  });});});
  out.sort(byTypeThenVariety);
  return out;
}
// What goes in a part's […]: its kind, then its brand ("Cotton (25–100%) ·
// Southworth"); a brand with no kind says so ("No type detail · Southworth"
// — a lone "[Southworth]" reads as a kind, as it always has).
function bracketOf(p){
  var b=p.brand&&hasBrand(p.type)?canonicalBrand(p.brand):null;
  if(!b)return p.variety||"";
  return(p.variety||NO_TYPE_DETAIL)+" · "+b;
}
// parts → "Items: Paper [Cotton (25–100%) · Southworth] $53.98 (2 · 200
// sheets) · Envelopes $68.98 (1)" ("" for none). The variety (and brand),
// when a part has one, goes in […] after the type.
function formatItemsLine(parts){
  if(!parts||!parts.length)return"";
  return ITEMS_PREFIX+parts.map(function(p){
    var inner=[];
    if(p.qty!=null)inner.push(fmtCount(p.qty));
    if(p.units!=null&&p.unitName)inner.push(fmtCount(p.units)+" "+(p.units===1?UNIT_ONE[p.unitName]||p.unitName:p.unitName));
    var br=bracketOf(p);
    return p.type+(br?" ["+br+"]":"")+" "+fmtMoney(p.amount)+(inner.length?" ("+inner.join(" · ")+")":"");
  }).join(" · ");
}
function itemsLine(items,rowAmount){return formatItemsLine(itemParts(items,rowAmount));}

function isItemsLine(line){return/^\s*items?\s*:/i.test(String(line||""));}
function hasItemsLine(notes){return String(notes||"").split("\n").some(isItemsLine);}
// Add the line under the ⚑ lines (the top of the comment); notes that
// already carry an Items line are left exactly as they are.
function withItemsLine(notes,line){
  if(!line)return notes||"";
  var n=String(notes||"");
  if(hasItemsLine(n))return n;
  if(!n.trim())return line;
  var lines=n.split("\n");var i=0;
  while(i<lines.length&&lines[i].indexOf(REVIEW_LINE)===0)i++;
  lines.splice(i,0,line);
  return lines.join("\n");
}
// Replace the Items line (or add one, under the ⚑ lines); "" / null just
// takes it out. Every other line is kept as it was.
function replaceItemsLine(notes,line){
  var rest=String(notes||"").split("\n").filter(function(l){return!isItemsLine(l);}).join("\n").replace(/^\n+|\n+$/g,"");
  return line?withItemsLine(rest,line):rest;
}
// Split at · ; | " + " and a comma before a word — never inside (…) / […]
// or a "$1,234.56".
function splitParts(body){
  var out=[];var depth=0;var cur="";
  for(var i=0;i<body.length;i++){
    var ch=body[i];
    if(ch==="("||ch==="[")depth++;
    else if(ch===")"||ch==="]")depth=Math.max(0,depth-1);
    var cut=false;
    if(!depth){
      if(ch==="·"||ch===";"||ch==="|")cut=true;
      else if(ch==="+"&&body[i-1]===" "&&body[i+1]===" ")cut=true;
      else if(ch===","&&/^\s*[A-Za-z]/.test(body.slice(i+1,i+4)))cut=true;
    }
    if(cut){out.push(cur);cur="";}else cur+=ch;
  }
  out.push(cur);
  return out.map(function(s){return s.trim();}).filter(Boolean);
}
var UNIT_WORDS={sheet:"sheets",sheets:"sheets",envelope:"envelopes",envelopes:"envelopes",label:"labels",labels:"labels",stamp:"stamps",stamps:"stamps",card:"cards",cards:"cards"};
// A part's "[…]" → {v: the kind's text, brand}. "[Cotton (25–100%) ·
// Southworth]", "[No type detail · Non-Southworth]": the segment that is a
// brand comes off. A lone "[Southworth]" / "[S/W]" (typed by hand) still
// reads its kind as it always has (Southworth paper = Cotton) AND is
// Southworth; a kind typed in words that name Southworth ("[southworth
// cotton]") is Southworth too — but never a kind's own label ("Premium
// (Southworth / cotton / linen)" is a kind, not a brand).
function splitBracket(type,text){
  var t=oneLine(text);
  if(!hasBrand(type))return{v:t,brand:null};
  var segs=t.split(/\s*·\s*/);
  if(segs.length>1){
    for(var i=segs.length-1;i>=0;i--){
      var b=brandToken(segs[i]);
      if(b!==undefined)return{v:segs.slice(0,i).concat(segs.slice(i+1)).join(" · "),brand:b};
    }
  }
  var only=brandToken(t);
  if(only===SOUTHWORTH)return{v:t,brand:only};
  if(only!==undefined)return{v:null,brand:only};
  var l=t.toLowerCase();
  var label=(VARIETIES[type]||[]).some(function(x){var k=x.v.toLowerCase();return k===l||k.split(" (")[0]===l;});
  return{v:t,brand:label?null:brandOf(t)};
}
// notes (or one line) → [{type, variety, brand?, amount, qty, units,
// unitName}] from its "Items:" line; [] when there is none. amount is null
// for a part typed without one. variety: the "[…]" after the type ("Paper
// [Cotton] $20"), or what a typed name says ("28 lb paper $40" → Paper,
// Premium 28 lb); null for an old line ("Paper $20"). brand (Paper /
// Envelopes / Cardstock — set only when there is one): the "[… ·
// Southworth]" / "[… · Non-Southworth]" in the brackets, or a typed name /
// kind that says Southworth ("Southworth envelopes $30"); typed words never
// make a part Non-Southworth (only the brackets do). An old line reads
// exactly as before (no brand key at all).
function parseItemsLine(notes){
  var line=null;
  String(notes||"").split("\n").some(function(l){if(isItemsLine(l)){line=l;return true;}return false;});
  if(!line)return[];
  var body=line.replace(/^\s*items?\s*:\s*/i,"");
  var out=[];
  splitParts(body).forEach(function(part){
    var inner="";var m=/\(([^)]*)\)\s*$/.exec(part);
    if(m){inner=m[1];part=part.slice(0,m.index).trim();}
    var am=/(-?\s*\$\s*-?\s*[\d,]*\.?\d+|-?[\d,]*\.\d{1,2}|-?\d[\d,]*)\s*$/.exec(part);
    var amount=null;var name=part;
    if(am&&am.index>0){
      var s=am[1].replace(/[\s$,]/g,"");var neg=/-/.test(s);s=s.replace(/-/g,"");
      var v=parseFloat(s);
      if(isFinite(v)){amount=Math.round((neg?-v:v)*100)/100;name=part.slice(0,am.index);}
    }
    name=name.replace(/[\s:=–—-]+$/,"").trim();
    var vtext=null;var bm=/\s*\[([^\]]*)\]\s*$/.exec(name)||/\s*\[([^\]]*)\]/.exec(name);
    if(bm){vtext=bm[1];name=(name.slice(0,bm.index)+" "+name.slice(bm.index+bm[0].length)).replace(/[\s:=–—-]+$/,"").trim();}
    var type=canonicalType(name);
    if(!type)return;
    var variety,brand=null;
    var named=name.toLowerCase()!==type.toLowerCase();
    if(vtext!=null){var sb=splitBracket(type,vtext);variety=sb.v!=null?canonicalVariety(type,sb.v):null;brand=sb.brand;}
    else variety=hasVarieties(type)&&named?namedVarietyOf(type,name):null;
    if(!brand&&hasBrand(type)&&named)brand=brandOf(name);
    var qty=null,units=null,unitName=null;
    inner.split(/\s*[·;]\s*|,\s+/).forEach(function(tok){   // never at "3,500"
      var t=oneLine(tok).toLowerCase();if(!t)return;
      var u=/^(\d[\d,]*)\s*([a-z]+)$/.exec(t);
      if(u&&UNIT_WORDS[u[2]]){units=countNum(u[1]);unitName=UNIT_WORDS[u[2]];return;}
      var q=/^(?:qty:?\s*|x\s*|×\s*)?(\d+(?:\.\d+)?)\s*(?:x|×|pcs?|units?|items?)?$/.exec(t);
      if(q&&qty==null)qty=Number(q[1]);
    });
    var o={type:type,variety:variety||null,amount:amount,qty:qty,units:units,unitName:unitName||(units!=null?unitNameOf(type):null)};
    if(brand)o.brand=brand;
    out.push(o);
  });
  return out;
}

// ── Expenses: what each expense went on ──────────────────────

// The old chip for a row nothing recognises: its own description.
function legacyLabel(ex){
  var clean=oneLine(ex&&ex.desc);
  if(clean)return clean.length>28?clean.slice(0,26)+"…":clean;
  return ex&&ex.vendor?oneLine(ex.vendor):"Uncategorized";
}
// An Amazon email subject (or a title cut short) names one item of the
// order at most — "Ordered: 2 "Columbian #10 Security..."" can cover four
// orders of paper AND envelopes. The importer never itemizes one (oneItem),
// so the Expenses screen doesn't type the row by it either: "No item
// detail" until the order report gives it its lines.
var AMAZON_SUBJECT_RE=/^(?:amazon(?:\.com| business)?\s*[—–-]\s*)?(?:(?:fwd?|re):\s*)?(?:ordered|shipped|delivered|out for delivery|arriving|refund|your refund|advance refund|return|your return|your (?:amazon(?:\.com)?(?: business)? )?order|order (?:confirmation|#|\d{3}-))\b/i;
function amazonEmailOnly(ex,group){
  if(group!=="Amazon")return false;
  var d=oneLine(ex&&ex.desc);
  return AMAZON_SUBJECT_RE.test(d)||/\.\.\.|…/.test(d);
}
// A hand-typed line with some parts priced and some not ("Items: Paper $20
// · Envelopes" on a $50 row): the priced parts keep their amounts and the
// rest of the row is split evenly over the unpriced ones (pennies on the
// first). → cents per part, or null when there is nothing left to split
// (the typed amounts already reach the row) — never a silently dropped part.
function fillUnpriced(parsed,amtC){
  if(!amtC)return null;
  var priced=parsed.filter(function(p){return p.amount!=null;}).map(function(p){return cents(p.amount);});
  // A refund typed as plain amounts ("Paper $20 · Envelopes" on −$50).
  var flip=amtC<0&&priced.every(function(c){return c>=0;})?-1:1;
  var typed=priced.reduce(function(s,c){return s+flip*c;},0);
  var rest=amtC-typed;
  var open=parsed.length-priced.length;
  if(!open||!rest||(rest>0)!==(amtC>0))return null;
  var each=Math.trunc(rest/open);var odd=rest-each*open;var k=0;
  return parsed.map(function(p){if(p.amount!=null)return flip*cents(p.amount);var c=each+(k===0?odd:0);k++;return c;});
}
// One expense → [{type, variety, brand, label, row, amount, cents, qty, units,
// unitName, fromLine, vendor, sub}] summing EXACTLY to its amount. From its Items line when
// it has one (a hand-typed line that doesn't add up to the amount is
// re-split in proportion — the tax usually; one with unpriced parts gives
// them what's left — fillUnpriced); otherwise ONE part for the whole
// amount: itemTypeOf(description) (then the vendor), "No item detail" for a
// store receipt that doesn't say (and for an Amazon email subject —
// amazonEmailOnly), else the description itself (the old chip; the table's
// row is Other).
function expenseItemParts(ex){
  var amtC=cents(ex&&ex.amount);
  var group=expenseVendorGroup(ex);
  var parsed=parseItemsLine(ex&&ex.notes);
  var got=null;
  if(parsed.length){
    var withAmt=parsed.filter(function(p){return p.amount!=null;});
    if(parsed.length===1&&!withAmt.length)got=[Object.assign({},parsed[0],{cents:amtC})];
    else if(withAmt.length===parsed.length){
      var cs=withAmt.map(function(p){return cents(p.amount);});
      var sum=cs.reduce(function(s,c){return s+c;},0);
      if(sum!==amtC)cs=spreadUntaxedPostage(amtC,cs,withAmt.map(function(p,i){return canonicalType(p.type)==="Postage"?cs[i]:0;}));
      if(cs)got=withAmt.map(function(p,i){return Object.assign({},p,{cents:cs[i]});});
    }else if(withAmt.length){
      var fc=fillUnpriced(parsed,amtC);
      if(fc)got=parsed.map(function(p,i){return Object.assign({},p,{cents:fc[i]});});
    }
  }
  var fromLine=!!got;
  if(!got){
    var t=fallbackItemType(ex,group);
    got=[{type:t||(isStoreGroup(group)?NO_DETAIL:null),variety:null,amount:null,qty:null,units:null,unitName:null,cents:amtC}];
  }
  // A hand-entered row's description names the kind ("28 lb paper",
  // "Southworth envelopes"): its one part of that type takes it (never an
  // imported row — "Amazon — <first title> (+2 more)" names one line of
  // several). The same for the brand, Southworth only (descBrand): his
  // own words never make a part Non-Southworth.
  var handV=null,handB=null;
  if(!(ex&&ex.imported)&&!looksImported(ex&&ex.notes)){
    var seen={};got.forEach(function(p){if(p.type)seen[p.type]=(seen[p.type]||0)+1;});
    handV=function(p){return!p.variety&&hasVarieties(p.type)&&seen[p.type]===1?descVariety(ex,p.type):p.variety||null;};
    handB=function(p){return!p.brand&&hasBrand(p.type)&&seen[p.type]===1?descBrand(ex):p.brand||null;};
  }
  // …and a count it names ("Southworth envelopes 500", "2 reams paper") is
  // that part's count (10/11/26 — his sister's-account purchases typed by
  // hand). Never an imported row's description, and never a stamp count
  // for postage the description calls add-on / parcel ("additional ounce
  // stamps 400" is 400 add-on stamps, not letter stamps).
  var handU=null;
  if(handV){
    var seenU={};got.forEach(function(p){if(p.type)seenU[p.type]=(seenU[p.type]||0)+1;});
    handU=function(p,v){if(p.units!=null||seenU[p.type]!==1)return null;if(p.type==="Postage"&&(v===POSTAGE_KINDS.ADDON||v===POSTAGE_KINDS.PARCEL))return null;var un=unitNameOf(p.type);return un?handCountOf(ex&&ex.desc,un):null;};
  }
  var mk=ex&&ex.cat==="Marketing";
  return got.map(function(p){
    var hv=handV?handV(p):(p.variety||null);
    var hu=handU?handU(p,hv):null;
    // kindGuess: the kind was read off a hand row's description, not typed
    // in the Items line's brackets (a typed Add-on / Parcel part is never
    // letter stamps; a guessed one yields to the line's letter-stamp count —
    // the stamp check's "Letter stamps it bought").
    return{type:p.type,variety:hv,kindGuess:!!(hv&&!p.variety),brand:hasBrand(p.type)?(handB?handB(p):(p.brand||null)):null,label:p.type||legacyLabel(ex),row:itemRowOf(p.type),amount:p.cents/100,cents:p.cents,qty:p.qty,
      units:hu!=null?hu:p.units,unitName:hu!=null?unitNameOf(p.type):p.unitName||(p.units!=null?unitNameOf(p.type):null),fromLine:fromLine,vendor:group,
      sub:mk?marketingSubOf(p.type):null};
  });
}
// The count a hand-typed description names, in `unitName`: what packCountOf
// reads ("500 envelopes", "2 reams paper"), else a number after the item word
// at the end ("Southworth envelopes 500", "Paper 2500", "Envelopes x500") —
// 10 or more, never a paper weight ("24 lb", "32#"), a year ("Stamps — Sept
// 2026") or an order / invoice number ("Business cards order 1042" —
// trailingCountOk). null when it names none.
function handCountOf(desc,unitName){
  var n=packCountOf(desc,unitName);if(n!=null)return n;
  var s=cleanText(desc);
  if(/\b\d{2,4} ?(lbs?|#|pounds?)\s*$/.test(s))return null;
  // (a number standing on its own — never the cents of a price, "@ $15.60")
  var m=/\b(envelopes?|envs?|sheets?|papers?|ppr|reams?|cards?|stamps?|labels?|cardstock)\b.*?\s(?:x|×)?\s?(\d{2,5})\s*$/.exec(s);
  if(!m)return null;
  var v=countNum(m[2]);
  if(!(v&&v>=10&&[20,24,28,32].indexOf(v)<0))return null;
  return trailingCountOk(s.slice(0,s.lastIndexOf(m[2])).replace(/(?:x|×)\s?$/,""),v)?v:null;
}
// An import stamp ("Imported from … · amazon:…", the old tracker stamp) in
// the notes — expenseImport's isStampLine, read loosely (this module imports
// nothing).
function looksImported(notes){return/(^|\n)\s*Imported from /.test(String(notes||""));}
// The kind a description names for a type — a rule only, never "Other"
// (never the vendor's name: "Amazon Business" isn't a business envelope).
function descVariety(ex,type){
  var d=oneLine(ex&&ex.desc);
  return d?namedVarietyOf(type,d):null;
}
// Southworth when a description says so ("Southworth envelopes", "S/W 32
// lb") — else null, never Non-Southworth, never the vendor's name.
function descBrand(ex){return brandOf(oneLine(ex&&ex.desc));}
// What a row with no Items line went on: its vendor + description together
// (a hand-logged "PropStream" with "Monthly subscription" is Data, not
// Software), then the description, then the vendor alone — never off an
// Amazon email subject (amazonEmailOnly). null = nothing says.
function fallbackItemType(ex,group){
  if(amazonEmailOnly(ex,group===undefined?expenseVendorGroup(ex):group))return null;
  var v=oneLine(ex&&ex.vendor),d=oneLine(ex&&ex.desc);
  return(v&&d?itemTypeOf(v+" "+d):null)||itemTypeOf(d)||itemTypeOf(v);
}

// ── Marketing subcategories (10/9/26) ────────────────────────
// Reece's four first (his words), then the other mailer item types, then
// "Other marketing" — whatever else a Marketing row went on (a store receipt
// with no item detail, office supplies on the same receipt, a description
// nothing recognises), so the subcategories add up to Marketing to the cent.
// (10/9/26, later: "Mailhouse" folded into Postage; "Business Cards" is its own.)
var MARKETING_SUBCATEGORIES=["Data","Postage","Paper","Envelopes","Cardstock","Ink & Toner","Printing","Business Cards","Labels","Pens & Markers","Mailing Machine",OTHER_MARKETING];
var MK_SUB={};MARKETING_SUBCATEGORIES.forEach(function(s,i){MK_SUB[s]=i;});
// An item type that is one of Marketing's own subcategories (not "Other marketing").
function isMarketingItem(type){return!!type&&type!==OTHER_MARKETING&&MK_SUB[type]!==undefined;}
// An item type → its Marketing subcategory (anything else → "Other marketing").
function marketingSubOf(type){return isMarketingItem(type)?type:OTHER_MARKETING;}
// A row's subcategories → [{sub, cents, amount, labels}] summing EXACTLY to
// its amount, in the order its parts come (ITEM_TYPES order), merged by
// subcategory. Every row is read as if it were Marketing (the caller decides
// whether it is). opts.partsOf: a cached expenseItemParts.
function subcategoryParts(ex,partsOf){
  var parts=(partsOf||expenseItemParts)(ex);
  var by=new Map();
  parts.forEach(function(p){
    var s=marketingSubOf(p.type);var g=by.get(s);
    if(!g){g={sub:s,cents:0,labels:[]};by.set(s,g);}
    g.cents+=p.cents;if(g.labels.indexOf(p.label)<0)g.labels.push(p.label);
  });
  return Array.from(by.values()).map(function(g){return{sub:g.sub,cents:g.cents,amount:g.cents/100,labels:g.labels};});
}
// "Paper + Envelopes" — a Marketing row's subcategories, biggest first (the
// CSV export's and the ready file's Subcategory column); "" outside Marketing.
function subcategoryText(ex,partsOf){
  if(!ex||ex.cat!=="Marketing")return"";
  return subcategoryParts(ex,partsOf).slice().sort(function(a,b){return Math.abs(b.cents)-Math.abs(a.cents);}).map(function(s){return s.sub;}).join(" + ");
}
// A subcategory as typed ("postage", "Mailhouse", "business cards") → one of
// MARKETING_SUBCATEGORIES, or null when it isn't one.
function canonicalSubcategory(text){
  var t=oneLine(text);if(!t)return null;
  if(t.toLowerCase()===OTHER_MARKETING.toLowerCase())return OTHER_MARKETING;
  var c=canonicalType(t);
  return c&&MK_SUB[c]!==undefined?c:null;
}
// The biggest of them (the log's Item chip on a Marketing row).
function mainSubcategory(ex,partsOf){
  var s=subcategoryParts(ex,partsOf);var best=s[0]||null;
  s.forEach(function(x){if(Math.abs(x.cents)>Math.abs(best.cents))best=x;});
  return best?best.sub:OTHER_MARKETING;
}
// What the Add / Edit form offers before Reece picks one: the subcategory
// its vendor + description say (Stamps → Postage, PropStream → Data).
function detectSubcategory(vendor,desc){
  return marketingSubOf(fallbackItemType({vendor:vendor,desc:desc}));
}
// A row filed under another category whose every part is a Marketing item
// (a hand-entered PropStream under Software is Data; "Stamps" under Other is
// Postage) — Expenses offers "Move to Marketing", never moves it itself.
function isMarketingItemRow(ex,partsOf){
  if(!ex||ex.cat==="Marketing")return false;
  var parts=(partsOf||expenseItemParts)(ex);
  return parts.length>0&&parts.every(function(p){return isMarketingItem(p.type)||!p.cents;})&&parts.some(function(p){return isMarketingItem(p.type);});
}
// The one-part Items line the Add / Edit form writes for a subcategory:
// "Items: Postage $50.00" (qty / units kept when given). variety / brand:
// the kind and brand the line already named ("Paper [Cotton (25–100%) ·
// Southworth] …") — an Edit that only changes the amount (or ticks Refund)
// keeps them.
function oneItemLine(type,amount,qty,units,unitName,variety,brand){
  if(!type)return"";
  // Postage kinds are a closed set (10/11/26): anything else isn't one.
  if(variety&&type==="Postage"&&!VARIETIES.Postage.some(function(x){return x.v===variety;}))variety=null;
  return formatItemsLine([{type:type,variety:variety&&hasVarieties(type)?variety:null,brand:brand&&hasBrand(type)?brand:null,amount:Math.round((Number(amount)||0)*100)/100,qty:qty==null?null:qty,units:units==null?null:units,unitName:units==null?null:(unitName||unitNameOf(type))}]);
}

// Put a count on ONE part of an expense (10/11/26 — Cost per letter's
// "How many?" boxes): → the new notes, or null when it can't be done here.
//   • no Items line: a one-part line for the whole amount with the count
//     ("Items: Envelopes $85.00 (1,000 envelopes)") — its kind / brand kept;
//   • a part read off the Items line: that part's count set (its qty, kind
//     and brand kept), every other part exactly as it was;
//   • units 0 / "" takes the count off;
//   • anything else (a part with no amount on the line, a type that isn't
//     counted) → null: put the count on that line in the Expense Log.
function withPartUnits(ex,partIndex,units){
  var n=units===""||units==null?0:Math.round(Number(units));
  if(!isFinite(n)||n<0)return null;
  var notes=String((ex&&ex.notes)||"");
  var parts=expenseItemParts(ex);
  var p=parts[partIndex];
  if(!p||!p.type)return null;
  var un=unitNameOf(p.type);if(!un)return null;
  if(!hasItemsLine(notes)){
    if(partIndex!==0||parts.length!==1)return null;
    if(!n)return notes;
    return withItemsLine(notes,oneItemLine(p.type,ex.amount,p.qty,n,un,p.variety,p.brand));
  }
  if(!p.fromLine)return null;
  var parsed=parseItemsLine(notes);
  if(parsed.length!==parts.length||parsed.some(function(x){return x.amount==null;}))return null;
  var q=parsed[partIndex];
  if(!q||q.type!==p.type)return null;
  if(n){q.units=n;q.unitName=un;}
  else{if(q.units==null)return notes;q.units=null;q.unitName=null;}
  return replaceItemsLine(notes,formatItemsLine(parsed));
}

// "3,500 sheets", "1 envelope".
function unitsLabel(units,unitName){
  if(units==null||!unitName)return"";
  return fmtCount(units)+" "+(units===1?UNIT_ONE[unitName]||unitName:unitName);
}
// "4.6¢ / envelope", "$0.13 / sheet".
function unitCostLabel(cost,unitName){
  if(!(cost>0)||!unitName)return"";
  var one=UNIT_ONE[unitName]||unitName;
  return(cost<0.1?(Math.round(cost*1000)/10).toFixed(1)+"¢":"$"+cost.toFixed(2))+" / "+one;
}
function newCell(){return{cents:0,ids:new Set(),units:0,unitsKnown:true,unitName:null};}
function addToCell(cell,ex,p){
  cell.cents+=p.cents;cell.ids.add(ex);
  if(p.unitName&&!cell.unitName)cell.unitName=p.unitName;
  if(p.units!=null)cell.units+=(p.cents<0?-1:1)*p.units;
  else if(p.cents)cell.unitsKnown=false;
}
function cellOut(cell){
  var unitCost=null;
  if(cell.unitName&&cell.unitsKnown&&cell.units>0&&cell.cents>0)unitCost=cell.cents/100/cell.units;
  return{cents:cell.cents,amount:cell.cents/100,n:cell.ids.size,units:cell.unitName&&cell.unitsKnown?cell.units:null,unitName:cell.unitName,unitCost:unitCost,unitCostLabel:unitCost?unitCostLabel(unitCost,cell.unitName):""};
}
// The "Where you buy it" table over these expenses → {columns, rows, total,
// spend, named}. A Paper / Envelopes / Cardstock row also says Southworth vs
// not (row.brands: [{brand, cents, amount, n, units, unitCost,
// unitCostLabel}] — Southworth, Non-Southworth, No brand detail; null when
// none of its parts has a brand). Columns: Amazon, Staples, then up to 3 more vendors by
// spend, then Other (every other vendor). Rows: item types, mailer supplies
// first. A cell = $ (parts of expenses), purchases (expenses with a part in
// it) and a unit cost only when the units behind EVERY dollar in it are
// known. Every part of every expense lands in exactly one cell, so the
// table's total IS the spend. opts.partsOf: a (cached) expenseItemParts.
function whereYouBuy(list,opts){
  opts=opts||{};
  var partsOf=opts.partsOf||expenseItemParts;
  var groups=new Map();   // vendor group → spend (cents)
  var entries=[];var spend=0;
  (list||[]).forEach(function(ex){
    if(!ex)return;
    var parts=partsOf(ex);var g=parts.length?parts[0].vendor:expenseVendorGroup(ex);
    spend+=cents(ex.amount);
    entries.push({ex:ex,parts:parts,g:g});
    groups.set(g,(groups.get(g)||0)+parts.reduce(function(s,p){return s+p.cents;},0));
  });
  var fixed=["Amazon","Staples"];
  var more=Array.from(groups.keys()).filter(function(g){return fixed.indexOf(g)<0&&g!==NO_VENDOR&&groups.get(g)>0;})
    .sort(function(a,b){return groups.get(b)-groups.get(a)||a.localeCompare(b);}).slice(0,3);
  var named=fixed.concat(more);
  var colOf=function(g){return named.indexOf(g)>=0?g:OTHER_VENDORS;};
  var rows=new Map();var cols=new Map();var tot=newCell();
  entries.forEach(function(e){
    var c=colOf(e.g);
    e.parts.forEach(function(p){
      var r=rows.get(p.row);if(!r){r={cells:new Map(),total:newCell(),brands:new Map(),branded:false};rows.set(p.row,r);}
      var cell=r.cells.get(c);if(!cell){cell=newCell();r.cells.set(c,cell);}
      addToCell(cell,e.ex,p);addToCell(r.total,e.ex,p);
      if(hasBrand(p.row)){
        var bk=brandKeyOf(p);if(bk!==NO_BRAND_DETAIL)r.branded=true;
        var bc=r.brands.get(bk);if(!bc){bc=newCell();r.brands.set(bk,bc);}
        addToCell(bc,e.ex,p);
      }
      var col=cols.get(c);if(!col){col=newCell();cols.set(c,col);}
      addToCell(col,e.ex,p);addToCell(tot,e.ex,p);
    });
  });
  var columns=named.map(function(g){return{key:g,label:g};});
  if(cols.has(OTHER_VENDORS))columns.push({key:OTHER_VENDORS,label:"Other"});
  columns.forEach(function(c){var x=cols.get(c.key);var o=x?cellOut(x):cellOut(newCell());c.amount=o.amount;c.cents=o.cents;c.n=o.n;});
  var rowList=Array.from(rows.keys()).sort(function(a,b){return typeOrder(a)-typeOrder(b)||a.localeCompare(b);}).map(function(k){
    var r=rows.get(k);var cells={};
    r.cells.forEach(function(cell,c){cells[c]=cellOut(cell);});
    var brands=r.branded?Array.from(r.brands.keys()).sort(function(a,b){return brandRank(a)-brandRank(b);}).map(function(b){return Object.assign({brand:b},cellOut(r.brands.get(b)));}):null;
    return{row:k,supply:isSupplyType(k),cells:cells,total:cellOut(r.total),brands:brands};
  });
  return{columns:columns,rows:rowList,total:cellOut(tot),spend:spend/100,spendCents:spend,named:named};
}
// Does this expense belong to a clicked cell ({row, vendor, named})? Vendor
// = its column (OTHER_VENDORS = every vendor not named), and one of its
// parts is in that row.
function cellMatches(ex,f,partsOf){
  if(!f)return true;
  var parts=(partsOf||expenseItemParts)(ex);
  var g=parts.length?parts[0].vendor:expenseVendorGroup(ex);
  var ok=!f.vendor||(f.vendor===OTHER_VENDORS?(f.named||[]).indexOf(g)<0:g===f.vendor);
  return ok&&parts.some(function(p){return p.row===f.row;});
}

export {
  ITEMS_PREFIX, ITEM_OTHER, NO_DETAIL, NO_VENDOR, OTHER_VENDORS, ITEM_TYPES, NAMED_SOFTWARE, OTHER_MARKETING, MARKETING_SUBCATEGORIES, NO_TYPE_DETAIL, VARIETIES,
  // taxonomy
  itemTypeOf, canonicalType, itemRowOf, unitNameOf, isSupplyType, packCountOf, handCountOf, fallbackItemType, isPostalService,
  // postage kinds + one receipt line's letter stamps (10/11/26, point-in-time cost per letter)
  POSTAGE_KINDS, postageVariety, stampLineFacts, letterRateOn, withPartUnits,
  // varieties (10/10/26)
  varietyOf, namedVarietyOf, canonicalVariety, hasVarieties, varietyRank, varietyNote, isOtherVariety,
  // brand: Southworth or not (10/10/26, later)
  SOUTHWORTH, NON_SOUTHWORTH, NO_BRAND_DETAIL, brandOf, upcIsSouthworth, otherBrandOf, columnBrand, hasBrand, canonicalBrand, brandKeyOf, brandRank, brandNote,
  // vendors
  vendorGroupOf, knownVendorGroup, expenseVendorGroup, isStoreGroup, dataSiteIn,
  // the Items line
  itemParts, mergeItemParts, formatItemsLine, itemsLine, parseItemsLine, isItemsLine, hasItemsLine, withItemsLine, replaceItemsLine, oneItemLine, itemDiscountLine, spread as spreadCents, spreadUntaxedPostage,
  // Expenses breakdowns
  expenseItemParts, whereYouBuy, cellMatches, unitCostLabel, unitsLabel, looksImported,
  // Marketing subcategories
  isMarketingItem, marketingSubOf, subcategoryParts, subcategoryText, canonicalSubcategory, mainSubcategory, detectSubcategory, isMarketingItemRow
};
